import { dashboardReportingCoverage, useDashboardCampaignFilter, useDashboardReporting } from "../hooks/useDashboardReporting";
import { REPORTING_TIME_ZONE } from "../utils/reportingCalendar";
// client/src/pages/Dashboard.js (CLIENT – Cortex Dashboard, smooth loader overlay like superadmin)

import { useLocation, useNavigate } from "react-router-dom";
import { buildCampaignsDeliveringTileModel } from "../platformAnalytics/campaignDeliveryAttribution";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./Dashboard.css";
import { artworkIntentHandlers } from "../utils/routeArtworkPreload";

import ClientPerformancePreview, {
  buildPlatformAnalyticsHandoffPath,
} from "../components/ClientPerformancePreview";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import {
  GovernedAdvertiserSelectorModal as DashboardAdvertiserSelector,
} from "../components/GovernedAdvertiserSelector";
import Loading from "../components/Loading";

import cphereHeaderLogo from "../assets/Cphere Logo New.png";
import cphereSocialImage from "../assets/tiles/cpheresocial.tile.jpg";
import integrationsImage from "../assets/tiles/creativestudio.tile.jpg";
import logoutImage from "../assets/tiles/logout.tile.png";
import profileSettingsImage from "../assets/tiles/profilesettings.tile.jpg";
import genesisImage from "../assets/tiles/genesis.tile.jpg";
import slackImage from "../assets/slack.png";
import appleMusicLogo from "../assets/applemusic.png";
import pandoraLogo from "../assets/pandora.png";
import spotifyLogo from "../assets/spotify.png";

import { useAdvertiser } from "../contexts/AdvertiserContext";
import { useAuth } from "../contexts/AuthContext";
import useResolvedImageCandidates from "../hooks/useResolvedImageCandidates";
import usePageAwareness from "../hooks/usePageAwareness";
import { buildEdenDashboardContext } from "../eden/context/dashboardContext";
import {
  buildAnalyticsMetricSignature,
  buildAnalyticsScopeKey,
  writeCachedAnalyticsIntelligence,
} from "../utils/analyticsIntelligenceCache";
import { resolveDisplaySpend } from "../utils/reportingTruthResolver";
import {
  getVerticalDashboardImagePreloadUrls,
  normalizeAdvertiserVertical,
  resolveVerticalDashboardConfigForAdvertiser,
} from "../config/verticalDashboardConfig";
import { buildAdvertiserLogoCandidates } from "../utils/advertiserLogo";
import { getCanonicalCampaignDisplayName } from "../utils/campaignDisplayName";

export {
  EdenEntryAttachmentBoundary,
  EdenEntryOverlay,
} from "../components/EdenEntryOverlay";
export { DashboardAdvertiserSelector };

// ✅ CRA-safe API base (no import.meta)
const normalizeBase = (base) => {
  if (!base) return "";
  let b = String(base).trim();
  b = b.replace(/\/+$/, "");
  if (b.toLowerCase().endsWith("/api")) b = b.slice(0, -4);
  return b;
};

const API_BASE_URL =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "https://cphere-backend.onrender.com";

const KEY_ID = "selectedAdvertiserId";
const KEY_NAME = "selectedAdvertiserName";
const WELCOME_SUMMARY_CACHE_KEY = "dashboard:welcome-summary:client:v3";
const DASHBOARD_FETCH_TIMEOUT_MS = 12000;
const DASHBOARD_LOADER_FAILSAFE_MS = 9000;
const WEATHER_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const SLACK_DEEP_LINK = "slack://open";
const SLACK_WEB_FALLBACK_URL = "https://app.slack.com/client";
const SLACK_FALLBACK_DELAY_MS = 1300;
export const PANDORA_INSTRUMENTAL_HIP_HOP_STATION = {
  label: "Instrumental Hip Hop Radio",
  stationId: "1175221906907669741",
  url: "https://www.pandora.com/station/play/1175221906907669741",
};
export const MUSIC_SERVICE_LINKS = [
  { label: "Apple Music", url: "https://music.apple.com", logo: appleMusicLogo },
  {
    label: "Pandora",
    url: PANDORA_INSTRUMENTAL_HIP_HOP_STATION.url,
    logo: pandoraLogo,
    stationLabel: PANDORA_INSTRUMENTAL_HIP_HOP_STATION.label,
    stationId: PANDORA_INSTRUMENTAL_HIP_HOP_STATION.stationId,
  },
  { label: "Spotify", url: "https://open.spotify.com", logo: spotifyLogo },
];

// ✅ handshake key App listens to
const DASH_READY_KEY = "cortexDashboardReady";

export function resolveDashboardVisibleAdvertisers(advertisers = [], accessIds = [], role = "client") {
  const normalizedRole = String(role || "").toLowerCase().replace(/[\s_]/g, "");
  if (normalizedRole === "superadmin") return Array.isArray(advertisers) ? advertisers : [];
  const accessSet = new Set((accessIds || []).map((id) => String(id)));
  return (Array.isArray(advertisers) ? advertisers : []).filter((advertiser) =>
    accessSet.has(String(advertiser?.id))
  );
}

export function resolveDashboardWelcomeBrandLogoCandidates({
  activeWelcomeSummary = null,
  selectedAdvertiserRecord = null,
  fallbackLogo = cphereHeaderLogo,
} = {}) {
  return [
    ...buildAdvertiserLogoCandidates(
      API_BASE_URL,
      selectedAdvertiserRecord,
      selectedAdvertiserRecord?.raw
    ),
    resolveBrandLogoUrl(resolveBrandLogo(activeWelcomeSummary)),
    resolveBrandLogoUrl(activeWelcomeSummary?.branding?.viewerBrandLogoUrl),
    resolveBrandLogoUrl(activeWelcomeSummary?.branding?.advertiserLogoUrl),
    ...buildAdvertiserLogoCandidates(
      API_BASE_URL,
      activeWelcomeSummary?.advertiser,
      activeWelcomeSummary?.advertiser?.raw
    ),
    fallbackLogo,
  ].filter((value, index, list) => value && list.indexOf(value) === index);
}

export function resolveDashboardWelcomeBrandLogoSrc({
  candidates = [],
  resolvedLogo = "",
  fallbackLogo = cphereHeaderLogo,
  failedLogos = [],
} = {}) {
  const failed = new Set((Array.isArray(failedLogos) ? failedLogos : []).filter(Boolean));
  const nextCandidate = (Array.isArray(candidates) ? candidates : []).find(
    (candidate) => candidate && !failed.has(candidate)
  );
  const usableResolvedLogo = resolvedLogo && !failed.has(resolvedLogo) ? resolvedLogo : "";
  if (nextCandidate && nextCandidate !== fallbackLogo) return nextCandidate;
  return usableResolvedLogo || nextCandidate || fallbackLogo || "";
}

// ✅ warm module caches so returning to Dashboard feels instant
let CORTEX_ASSETS_WARM = false;
let CORTEX_ASSET_MAP_WARM = null;

function formatMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: numeric >= 100 ? 0 : 2,
  }).format(numeric);
}

function formatDashboardCurrency(value, maximumFractionDigits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: maximumFractionDigits,
    maximumFractionDigits,
  }).format(numeric);
}

function formatDashboardInteger(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return Math.round(numeric).toLocaleString("en-US");
}

function formatDashboardPercent(value, maximumFractionDigits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return `${numeric.toFixed(maximumFractionDigits)}%`;
}

const DASHBOARD_TOPLINE_VISIBLE_META = {
  "canonical dashboard spend": "Active Campaign Spend to Date",
  "canonical performance summary": "Active Campaign Spend to Date",
  "client-billed active campaigns to date": "Active Campaign Spend to Date",
  "canonical delivery volume": "Total Campaign Impressions",
  "canonical traffic response": "Total Clicks",
  "canonical blended ctr": "Click-Through Rate",
  "canonical click efficiency": "Cost Per Click",
  "canonical impression efficiency": "Cost Per Thousand Impressions",
};

function formatDashboardToplineMeta(value, fallback = "") {
  const source = String(value || fallback || "").trim();
  if (!source) return "";

  const mapped = Object.entries(DASHBOARD_TOPLINE_VISIBLE_META).reduce(
    (copy, [internalLabel, clientLabel]) =>
      copy.replace(new RegExp(internalLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), clientLabel),
    source
  );

  return mapped.replace(/\bcanonical\b\s*/gi, "").replace(/\s+/g, " ").trim();
}

function firstFiniteDashboardNumber(...values) {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

export function getWelcomeDisplaySpendToDate(summary) {
  const metrics = summary?.metrics || {};
  return resolveDisplaySpend(metrics, metrics.spendToDate ?? null);
}

/*
 * "Platforms Live" — read from the backend, never reconstructed here.
 *
 * The count is derived once, server-side, from runtime evidence (executed
 * trafficking activation, or delivery observed inside the current flight and
 * inside a trailing recency window). See services/platformLivenessService.js.
 *
 * This portal's only job is to print it. It must NOT fall back to counting
 * approved allocations, platform cards, configured integrations, or the
 * presence of an analytics series — every one of those is a readiness signal,
 * and a fallback built on readiness is exactly how a configured-but-dark
 * platform got counted as running. With no canonical value the honest answer
 * is 0, and the backend diagnostics (`metrics.platformsLiveBasis`) say why.
 *
 * `platformsRunning` is the same backend number under its historical key, kept
 * only so a payload cached before the rename still renders; it is not a second
 * derivation and there is no third.
 */
/*
 * "Campaigns Delivering" — read from the backend's evidence-based headline,
 * never reconstructed here. The count is campaigns with CONFIRMED delivery at
 * the latest complete reporting cutoff (`metrics.campaignsDelivering`), and
 * the supporting copy separates "inside an approved flight" from "delivering
 * outside its flight" (`metrics.deliveryScope`). Approved-flight membership —
 * the approved-in-flight basis — is neither the headline nor a fallback for a
 * fresh payload; it survives only for a payload cached before the contract
 * existed. The tile reads "Campaigns Live" in every state; the evidence state
 * is carried by the value and the meta line. See services/deliveryObservationScopeService.js.
 */
export function getWelcomeCampaignsDeliveringTile(summary) {
  return buildCampaignsDeliveringTileModel(summary);
}

export function getWelcomeDisplayPlatformsLive(summary) {
  const metrics = summary?.metrics || {};
  const raw = metrics.platformsLive ?? metrics.platformsRunning ?? null;
  const count = Number(raw);
  return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
}

function resolveBrandLogo(summary) {
  return summary?.branding?.resolvedBrandLogoUrl || "";
}

const isAbortLikeError = (error) =>
  error?.name === "AbortError" || error?.code === 20 || /abort/i.test(String(error?.message || ""));

const fetchWithDashboardTimeout = async (url, options = {}, timeoutMs = DASHBOARD_FETCH_TIMEOUT_MS) => {
  const timeoutController = new AbortController();
  const parentSignal = options.signal;
  let timeoutId = null;

  const abort = () => {
    try {
      timeoutController.abort();
    } catch {}
  };

  if (parentSignal?.aborted) abort();
  else if (parentSignal) parentSignal.addEventListener("abort", abort, { once: true });

  timeoutId = window.setTimeout(abort, timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: timeoutController.signal,
    });
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
    if (parentSignal) parentSignal.removeEventListener("abort", abort);
  }
};

// The dashboard's "today" is the reporting calendar's day (Eastern Time),
// never the device's: days remaining, the lifecycle state and the date EDEN is
// given are the same for every viewer.
function getDashboardTimeZone() {
  return REPORTING_TIME_ZONE;
}

function getDashboardLocalDateKey(timeZone = getDashboardTimeZone()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${lookup.year}-${lookup.month}-${lookup.day}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

const DASHBOARD_DAY_MS = 24 * 60 * 60 * 1000;
const DASHBOARD_HOUR_MS = 60 * 60 * 1000;
const EMPTY_CAMPAIGN_DAYS_MODEL = Object.freeze({
  value: "--",
  label: "No Active Campaign",
  detail: "Flight Window Pending",
  state: "empty",
});
const MISSING_CAMPAIGN_DATES_MODEL = Object.freeze({
  value: "--",
  label: "Campaign Status",
  detail: "Flight Dates Pending",
  state: "missing_dates",
});

function toDashboardDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function toDashboardDate(value) {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function dashboardDateKeyToUtcMs(value) {
  const dateKey = toDashboardDateKey(value);
  const match = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function dashboardDaysBetween(startDateKey, endDateKey) {
  const startMs = dashboardDateKeyToUtcMs(startDateKey);
  const endMs = dashboardDateKeyToUtcMs(endDateKey);
  if (startMs == null || endMs == null) return null;
  return Math.round((endMs - startMs) / DASHBOARD_DAY_MS);
}

function formatDashboardShortDate(dateKey) {
  const ms = dashboardDateKeyToUtcMs(dateKey);
  if (ms == null) return "";
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  }).format(new Date(ms));
  return formatted.toUpperCase();
}

function getDashboardCampaignDateValue(value, keys = []) {
  if (!value || typeof value !== "object") return null;
  for (const key of keys) {
    if (value[key]) return value[key];
  }
  return null;
}

export function formatDashboardDateParts(date = new Date()) {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).formatToParts(safeDate);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const month = String(lookup.month || "").toUpperCase();
  const day = String(lookup.day || "");
  return {
    dayNumber: day || "--",
    monthDay: month && day ? `${month} ${day}` : "DATE PENDING",
    weekday: String(lookup.weekday || "LOCAL DATE").toUpperCase(),
    year: String(lookup.year || ""),
  };
}

export function formatDashboardCurrentTime(date = new Date()) {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(safeDate);
}

export function getTimeAwareGreeting(date = new Date()) {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const hour = safeDate.getHours();
  if (hour >= 5 && hour < 12) return "Good Morning";
  if (hour >= 12 && hour < 17) return "Good Afternoon";
  return "Good Evening";
}

export function getTimeAwareWelcomeHeadline({ date = new Date(), name = "" } = {}) {
  const greeting = getTimeAwareGreeting(date);
  const trimmedName = String(name || "").trim();
  return trimmedName ? `${greeting}, ${trimmedName}` : greeting;
}

export function resolveDashboardViewerDisplayName(...sources) {
  const displayNameFields = ["firstName", "preferredDisplayName", "displayName", "name", "fullName"];

  for (const source of sources) {
    if (!source) continue;
    if (typeof source === "string") {
      const trimmedSource = source.trim();
      if (trimmedSource) return trimmedSource;
      continue;
    }
    if (typeof source !== "object") continue;

    for (const field of displayNameFields) {
      const candidate = String(source[field] || "").trim();
      if (candidate) return candidate;
    }
  }

  return "";
}

function getStoredDashboardViewerDisplayName() {
  if (typeof window === "undefined" || !window.localStorage) return "";

  try {
    return resolveDashboardViewerDisplayName({
      preferredDisplayName: window.localStorage.getItem("preferredDisplayName"),
      firstName: window.localStorage.getItem("firstName"),
      displayName: window.localStorage.getItem("displayName"),
      name: window.localStorage.getItem("userName"),
    });
  } catch {
    return "";
  }
}

function resolveDashboardCanonicalKpiMetrics(summary = null) {
  const canonicalSummary =
    summary?.canonicalSummary ||
    summary?.canonicalReporting ||
    summary?.canonicalMetrics?.summary ||
    null;
  const canonicalMetrics =
    canonicalSummary?.aggregateTotals ||
    canonicalSummary?.metrics ||
    summary?.canonicalMetrics?.aggregateTotals ||
    summary?.canonicalMetrics?.metrics ||
    {};
  const welcomeMetrics = summary?.metrics || {};

  return {
    spend: firstFiniteDashboardNumber(
      canonicalMetrics.clientFacingSpend,
      canonicalMetrics.spendClientFacing,
      canonicalMetrics.spend,
      canonicalMetrics.adSpend,
      welcomeMetrics.clientFacingSpend,
      welcomeMetrics.spendClientFacing,
      welcomeMetrics.spendToDate
    ),
    impressions: firstFiniteDashboardNumber(
      canonicalMetrics.impressions,
      canonicalMetrics.clientFacingImpressions,
      welcomeMetrics.impressions
    ),
    clicks: firstFiniteDashboardNumber(canonicalMetrics.clicks, welcomeMetrics.clicks),
    ctr: firstFiniteDashboardNumber(canonicalMetrics.ctr, welcomeMetrics.ctr),
    cpc: firstFiniteDashboardNumber(
      canonicalMetrics.cpc,
      canonicalMetrics.clientFacingCpc,
      welcomeMetrics.cpc
    ),
    cpm: firstFiniteDashboardNumber(
      canonicalMetrics.cpm,
      canonicalMetrics.effectiveCpm,
      canonicalMetrics.clientFacingCpm,
      welcomeMetrics.cpm
    ),
    conversions: firstFiniteDashboardNumber(
      canonicalMetrics.conversions,
      canonicalMetrics.clientFacingConversions,
      welcomeMetrics.conversions
    ),
    spendLabel:
      canonicalSummary?.source?.label ||
      welcomeMetrics.spendToDateLabel ||
      "Active Campaign Spend to Date",
  };
}

function resolveDashboardAdvertiserLabel(summary = null) {
  return (
    String(
      summary?.advertiser?.name ||
        summary?.advertiserName ||
        summary?.selectedAdvertiserName ||
        summary?.campaign?.advertiserName ||
        ""
    ).trim() || "The campaign"
  );
}

const DASHBOARD_EDEN_NO_ACTIVITY_ANALYSIS =
  "No active campaign delivery is available for this advertiser yet. Once spend, impressions, clicks, or response signals begin populating, Eden will summarize performance patterns and optimization opportunities here.";

const DASHBOARD_EDEN_BANNED_ANALYSIS_PHRASES = [
  "strong current traffic read",
  "response and cost efficiency moving in the same direction",
  "early interest signal",
  "conversion capture should stay the watchpoint",
  "monitoring whether added spend begins to dilute response quality",
  "verified delivery signals",
  "downstream intent",
  "blanket platform win",
  "no downstream action field",
  "in this read",
  "should stay anchored",
  "before making lower-funnel claims",
  "source-of-truth",
  "KPI read",
  "decision baseline",
  "current traffic read",
  "cross-channel",
  "canonical",
  "fallback",
  "resolver",
  "raw spend",
  "payload",
  "contract",
  "snapshot",
  "cache",
  "rollup",
];

const DASHBOARD_EDEN_BLOCKED_ANALYSIS_PATTERN =
  /\braw\b|source-of-truth|verified delivery signals|downstream intent|blanket platform win|no downstream action field|should stay anchored|before making lower-funnel claims/i;

function containsDashboardEdenBlockedLanguage(value = "") {
  const text = String(value || "").toLowerCase();
  return (
    DASHBOARD_EDEN_BLOCKED_ANALYSIS_PATTERN.test(text) ||
    DASHBOARD_EDEN_BANNED_ANALYSIS_PHRASES.some((phrase) =>
      text.includes(String(phrase).toLowerCase())
    )
  );
}

function hasDashboardMeaningfulToplineActivity(metrics = {}) {
  return [
    metrics.spend,
    metrics.impressions,
    metrics.clicks,
    metrics.conversions,
  ].some((value) => Number.isFinite(value) && value > 0);
}

function resolveDashboardCampaignLabel(summary = null) {
  // Canonical identity rule: run every campaign candidate through the shared
  // display-name precedence (live Meta name > linked/external > reporting >
  // internal) instead of reading raw campaign_name fields, so Dashboard labels
  // always match the campaign dropdowns and Sherloc Reports.
  const campaignCandidates = [
    summary?.campaign,
    summary?.activeCampaign,
    summary?.currentCampaign,
    summary?.selectedCampaign,
    summary?.canonicalSummary?.campaignScope?.activeCampaigns?.[0],
  ];
  for (const candidate of campaignCandidates) {
    const name = getCanonicalCampaignDisplayName(candidate);
    if (name) return name;
  }
  return (
    String(summary?.campaignName || summary?.campaign_name || "").trim() ||
    resolveDashboardAdvertiserLabel(summary)
  );
}

function resolveDashboardVertical(summary = null) {
  return normalizeAdvertiserVertical(
    summary?.advertiser?.vertical ||
      summary?.vertical ||
      summary?.campaign?.vertical ||
      summary?.activeCampaign?.vertical ||
      summary?.canonicalSummary?.campaignScope?.activeCampaigns?.[0]?.vertical ||
      ""
  );
}

function isDashboardFilmVertical(summary = null) {
  const vertical = resolveDashboardVertical(summary);
  const text = `${vertical} ${summary?.advertiser?.vertical || ""} ${summary?.campaign?.vertical || ""}`;
  return /film|cinema|theatrical|movie|entertainment/i.test(text);
}

function resolveDashboardFlightStage(summary = null) {
  const window = resolveDashboardCampaignWindow(summary);
  if (!window?.start || !window?.end) return "";
  const today = toDashboardDateKey(summary?.todayDateKey || getDashboardLocalDateKey(getDashboardTimeZone()));
  const totalDays = dashboardDaysBetween(window.start, window.end) + 1;
  const elapsedDays = dashboardDaysBetween(window.start, today) + 1;
  if (!Number.isFinite(totalDays) || !Number.isFinite(elapsedDays)) return "";
  if (elapsedDays <= 0) return "pre-flight";
  if (elapsedDays >= totalDays) return "final flight day";
  const pct = elapsedDays / totalDays;
  if (pct >= 0.75) return "late flight";
  if (pct >= 0.35) return "mid-flight";
  return "early flight";
}

function normalizeDashboardPlatformLabel(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (normalized.includes("youtube")) return "YouTube";
  if (normalized.includes("instagram")) return "Instagram";
  if (normalized.includes("facebook")) return "Facebook";
  if (normalized.includes("meta")) return "Meta";
  if (normalized.includes("ctv") || normalized.includes("connectedtv")) return "CTV";
  if (normalized.includes("display")) return "Display";
  if (normalized.includes("tiktok")) return "TikTok";
  return raw.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeDashboardPlatformRows(summary = null) {
  const canonicalSummary =
    summary?.canonicalSummary ||
    summary?.canonicalReporting ||
    summary?.canonicalMetrics?.summary ||
    {};
  const candidates = [
    canonicalSummary.platformRows,
    canonicalSummary.platforms,
    canonicalSummary.platformBreakdown,
    canonicalSummary.byPlatform,
    canonicalSummary.metricsByPlatform,
    summary?.platformRows,
    summary?.platforms,
    summary?.byPlatform,
  ].filter(Boolean);

  return candidates.flatMap((candidate) => {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") {
      return Object.entries(candidate).map(([key, value]) => ({
        ...(value && typeof value === "object" ? value : {}),
        platform: value?.platform || value?.label || key,
      }));
    }
    return [];
  }).map((row) => ({
    label: normalizeDashboardPlatformLabel(row.platform || row.platformLabel || row.label || row.name || row.key),
    spend: firstFiniteDashboardNumber(row.spend, row.clientFacingSpend, row.billedSpend),
    impressions: firstFiniteDashboardNumber(row.impressions),
    clicks: firstFiniteDashboardNumber(row.clicks),
    ctr: firstFiniteDashboardNumber(row.ctr),
    cpc: firstFiniteDashboardNumber(row.cpc),
    cpm: firstFiniteDashboardNumber(row.cpm),
    videoViews: firstFiniteDashboardNumber(row.videoViews, row.video_views, row.views),
    videoCompletions: firstFiniteDashboardNumber(row.videoCompletions, row.video_completions, row.completions),
    conversions: firstFiniteDashboardNumber(row.conversions, row.ticketSales),
  })).filter((row) => row.label);
}

function resolveDashboardLeader(rows = [], preferredMetrics = []) {
  for (const metric of preferredMetrics) {
    const winner = rows
      .filter((row) => Number.isFinite(row[metric]) && row[metric] > 0)
      .sort((a, b) => b[metric] - a[metric])[0];
    if (winner) return { row: winner, metric };
  }
  return null;
}

function formatDashboardLeaderBasis(metric) {
  return {
    spend: "spend",
    impressions: "impressions",
    clicks: "clicks",
    ctr: "CTR",
    videoViews: "video views",
    videoCompletions: "video completions",
    conversions: "conversions",
  }[metric] || metric;
}

function buildDashboardKpiProof(metrics = {}) {
  const proof = [];
  if (Number.isFinite(metrics.impressions) && metrics.impressions > 0) {
    proof.push(`${formatDashboardInteger(metrics.impressions)} impressions`);
  }
  if (Number.isFinite(metrics.clicks) && metrics.clicks > 0) {
    proof.push(`${formatDashboardInteger(metrics.clicks)} clicks`);
  }
  if (Number.isFinite(metrics.ctr) && metrics.ctr > 0) {
    proof.push(`${formatDashboardPercent(metrics.ctr)} blended CTR`);
  }
  if (proof.length >= 3) return `${proof[0]}, ${proof[1]}, and ${proof[2]}`;
  if (proof.length === 2) return `${proof[0]} and ${proof[1]}`;
  return proof[0] || "";
}

function buildDashboardLeaderSentence(leader) {
  if (!leader?.row?.label) return "";
  const label = leader.row.label;
  const basis = formatDashboardLeaderBasis(leader.metric);
  const role = {
    videoViews: "making it the clearest video engagement and audience-discovery lever",
    videoCompletions: "making it the clearest completed-view and creative-attention lever",
    impressions: "making it the primary reach and awareness lever",
    clicks: "making it the strongest traffic-contribution lever",
    ctr: "making it the strongest click-efficiency signal",
    spend: "making it the main scale and learning lever",
    conversions: "making it the strongest observed response path",
  }[leader.metric] || "making it an important performance lever";
  return `${label} is leading by ${basis}, ${role}.`;
}

function buildDashboardNextStep({ isFilm, hasConversionCapture, stage }) {
  const isClosingFlight = /late|final|ended/i.test(String(stage || ""));
  if (hasConversionCapture) {
    return isClosingFlight
      ? "As the flight closes, the next step is to preserve the paths producing efficient conversions and carry those audience and creative learnings into the next campaign phase."
      : "The next optimization focus should be protecting the audience, creative, and placement combinations producing efficient conversions while trimming weaker response paths.";
  }
  if (isFilm) {
    return isClosingFlight
      ? "As the release window closes, the next step is to isolate the creative and audience pairings that produced the strongest discovery and carry them into the next title push."
      : "The next optimization focus should be strengthening the platforms and creative cuts producing the best trailer engagement and audience discovery.";
  }
  return isClosingFlight
    ? "As the flight closes, the key next step is to isolate the strongest creative and audience pairings and use those learnings to guide the next campaign phase."
    : "The next optimization focus should be identifying which creative, audience, and placement combinations are producing the most efficient clicks.";
}

function buildDashboardEdenDeterministicAnalysis(summary, metrics) {
  const advertiser = resolveDashboardAdvertiserLabel(summary);
  const campaign = resolveDashboardCampaignLabel(summary);
  const stage = resolveDashboardFlightStage(summary);
  const isFilm = isDashboardFilmVertical(summary);
  const rows = normalizeDashboardPlatformRows(summary);
  const hasConversions = Number.isFinite(metrics.conversions);
  const hasConversionCapture = hasConversions && metrics.conversions > 0;
  const leader = resolveDashboardLeader(
    rows,
    isFilm
      ? ["videoViews", "videoCompletions", "impressions", "clicks", "spend"]
      : hasConversionCapture
      ? ["conversions", "clicks", "ctr", "spend", "impressions"]
      : ["clicks", "impressions", "ctr", "spend"]
  );
  const campaignLabel = campaign && campaign !== advertiser ? campaign : advertiser;
  const sentences = [];
  const proof = buildDashboardKpiProof(metrics);
  const proofClause = proof ? `, with ${proof}` : "";

  if (isFilm) {
    sentences.push(
      `${campaignLabel} is delivering theatrical awareness and traffic performance${stage ? ` in ${stage}` : ""}${proofClause}.`
    );
  } else if (hasConversionCapture) {
    sentences.push(
      `${campaignLabel} is showing measurable campaign response${proofClause}.`
    );
  } else if (Number.isFinite(metrics.ctr) && metrics.ctr >= 2.5 && Number.isFinite(metrics.cpc) && metrics.cpc <= 0.75) {
    sentences.push(
      `${campaignLabel} is showing efficient cross-platform engagement${proofClause}.`
    );
  } else if (Number.isFinite(metrics.ctr) && metrics.ctr < 1) {
    sentences.push(
      `${campaignLabel} is building reach ahead of click response${proofClause}, making audience and placement selectivity the immediate opportunity.`
    );
  } else {
    sentences.push(
      `${campaignLabel} has enough live delivery to guide a cross-platform optimization read${proofClause}.`
    );
  }

  if (leader) {
    sentences.push(buildDashboardLeaderSentence(leader));
  } else if (Number.isFinite(metrics.impressions) && Number.isFinite(metrics.clicks)) {
    sentences.push(
      "The aggregate pattern shows both reach and click activity, which makes creative and audience pairing the clearest learning area."
    );
  }

  sentences.push(buildDashboardNextStep({ isFilm, hasConversionCapture, stage }));

  return `Brief Analysis: ${sentences.join(" ")}`;
}

function ensureDashboardEdenAnalysisQuality(summary, metrics) {
  const firstPass = buildDashboardEdenDeterministicAnalysis(summary, metrics);
  if (!containsDashboardEdenBlockedLanguage(firstPass)) return firstPass;
  const campaign = resolveDashboardCampaignLabel(summary);
  const advertiser = resolveDashboardAdvertiserLabel(summary);
  const label = campaign && campaign !== advertiser ? campaign : advertiser;
  return `Brief Analysis: ${label} has live delivery to evaluate, with spend, reach, clicks, and efficiency ready to guide the next optimization pass. The next step is to compare platform contribution against creative and audience pairings before reallocating budget.`;
}

export function buildDashboardEdenToplineMetrics(summary = null) {
  const metrics = resolveDashboardCanonicalKpiMetrics(summary);
  return [
    metrics.spend !== null
      ? {
          id: "dashboard_kpi_spend",
          label: "Spend",
          value: formatDashboardCurrency(metrics.spend, metrics.spend >= 100 ? 0 : 2),
          meta: formatDashboardToplineMeta(metrics.spendLabel, "Active Campaign Spend to Date"),
        }
      : null,
    metrics.impressions !== null
      ? {
          id: "dashboard_kpi_impressions",
          label: "Impressions",
          value: formatDashboardInteger(metrics.impressions),
          meta: "Total Campaign Impressions",
        }
      : null,
    metrics.clicks !== null
      ? {
          id: "dashboard_kpi_clicks",
          label: "Clicks",
          value: formatDashboardInteger(metrics.clicks),
          meta: "Total Clicks",
        }
      : null,
    metrics.ctr !== null
      ? {
          id: "dashboard_kpi_ctr",
          label: "Blended CTR",
          value: formatDashboardPercent(metrics.ctr),
          meta: "Cross-platform blended CTR",
        }
      : null,
    metrics.cpc !== null
      ? {
          id: "dashboard_kpi_cpc",
          label: "CPC",
          value: formatDashboardCurrency(metrics.cpc),
          meta: "Cost Per Click",
        }
      : null,
    metrics.cpm !== null
      ? {
          id: "dashboard_kpi_cpm",
          label: "CPM",
          value: formatDashboardCurrency(metrics.cpm),
          meta: "Cost Per Thousand Impressions",
        }
      : null,
  ].filter(Boolean);
}

function resolveDashboardToplineThroughLabel(summary = null) {
  const timeframe =
    summary?.canonicalSummary?.timeframe ||
    summary?.canonicalReporting?.timeframe ||
    summary?.metrics?.spendToDateWindow ||
    null;
  const endDateKey = toDashboardDateKey(
    timeframe?.end ||
      timeframe?.dateEnd ||
      timeframe?.endDate ||
      timeframe?.through ||
      ""
  );

  return endDateKey ? `through ${formatDashboardShortDate(endDateKey)}` : "campaign to date";
}

export function buildDashboardEdenToplineInsight(summary = null) {
  const metrics = resolveDashboardCanonicalKpiMetrics(summary);
  const throughLabel = resolveDashboardToplineThroughLabel(summary);
  const parts = [
    metrics.spend !== null
      ? `${formatDashboardCurrency(metrics.spend, metrics.spend >= 100 ? 0 : 2)} active campaign spend`
      : "",
    metrics.impressions !== null
      ? `${formatDashboardInteger(metrics.impressions)} impressions`
      : "",
    metrics.clicks !== null ? `${formatDashboardInteger(metrics.clicks)} clicks` : "",
    metrics.ctr !== null ? `${formatDashboardPercent(metrics.ctr)} blended CTR` : "",
    metrics.cpc !== null ? `${formatDashboardCurrency(metrics.cpc)} CPC` : "",
    metrics.cpm !== null ? `${formatDashboardCurrency(metrics.cpm)} CPM` : "",
  ].filter(Boolean);

  return parts.length ? `Campaign Performance Summary: ${parts.join(", ")} ${throughLabel}.` : "";
}

export function buildDashboardEdenToplineAnalysis(summary = null) {
  const metrics = resolveDashboardCanonicalKpiMetrics(summary);
  if (!hasDashboardMeaningfulToplineActivity(metrics)) {
    return DASHBOARD_EDEN_NO_ACTIVITY_ANALYSIS;
  }

  const hasTrafficRead =
    metrics.impressions !== null || metrics.clicks !== null || metrics.ctr !== null || metrics.cpc !== null;
  if (!hasTrafficRead && metrics.spend === null) return "";

  return ensureDashboardEdenAnalysisQuality(summary, metrics);
}

function extractCampaignWindow(value) {
  if (!value || typeof value !== "object") return null;

  const nestedWindow =
    value.flightWindow ||
    value.flight ||
    value.campaignWindow ||
    value.campaignFlightWindow ||
    null;
  if (nestedWindow && nestedWindow !== value) {
    const nested = extractCampaignWindow(nestedWindow);
    if (nested?.start || nested?.end) return nested;
  }

  const startRaw = getDashboardCampaignDateValue(value, [
    "start",
    "startDate",
    "start_date",
    "flightStart",
    "flight_start",
    "flight_start_date",
    "flightStartDate",
    "dateStart",
    "date_start",
  ]);
  const endRaw = getDashboardCampaignDateValue(value, [
    "end",
    "endDate",
    "end_date",
    "flightEnd",
    "flight_end",
    "flight_end_date",
    "flightEndDate",
    "dateEnd",
    "date_end",
  ]);
  const start = toDashboardDateKey(startRaw);
  const end = toDashboardDateKey(endRaw);

  return start || end ? { start, end, startRaw, endRaw } : null;
}

// Campaign lifecycle card eligibility. Approved/committed campaigns plus
// client-facing PENDING proposals with committed flight dates count as
// launch-bound; DRAFT work and read-only imported reporting campaigns
// (REPORTING_ONLY) never produce a Launch Countdown or Active Campaign state.
// NEEDS_REAPPROVAL is launch-bound: it is only entered FROM an approved state
// (plan revision pending re-approval) and the last-approved flight keeps
// delivering — the card must not fall to "No Active Campaign" mid-flight
// (Aug 17 2026 incident; mirrors the backend committed-reportable primitive).
const DASHBOARD_LAUNCH_BOUND_CAMPAIGN_STATUSES = new Set([
  "APPROVED",
  "COMPLETED",
  "EXECUTED",
  "COUNTERSIGNED",
  "NEEDS_REAPPROVAL",
  "PENDING",
]);

function isDashboardReportingOnlyCampaign(candidate) {
  if (!candidate || typeof candidate !== "object") return false;
  if (String(candidate.status || "").trim().toUpperCase() === "REPORTING_ONLY") return true;
  if (candidate.reportingOnly === true) return true;
  if (
    candidate.externalReporting &&
    typeof candidate.externalReporting === "object" &&
    candidate.externalReporting.readOnly === true
  ) {
    return true;
  }
  const rawPayload = candidate.strategy_payload ?? candidate.strategyPayload;
  let payload = rawPayload;
  if (typeof rawPayload === "string") {
    try {
      payload = JSON.parse(rawPayload);
    } catch {
      payload = null;
    }
  }
  if (!payload || typeof payload !== "object") return false;
  if (payload.reportingOnly === true) return true;
  return Boolean(
    payload.externalReporting &&
      typeof payload.externalReporting === "object" &&
      payload.externalReporting.readOnly === true
  );
}

function isDashboardLaunchBoundCampaignCandidate(candidate) {
  if (!candidate || typeof candidate !== "object") return false;
  if (isDashboardReportingOnlyCampaign(candidate)) return false;
  if (candidate.countersigned_at || candidate.countersignedAt) return true;
  const statuses = [
    candidate.contract_status,
    candidate.contractStatus,
    candidate.authorization_status,
    candidate.authorizationStatus,
    candidate.status,
  ]
    .map((status) => String(status || "").trim().toUpperCase())
    .filter(Boolean);
  // Plain flight windows (canonical summary scopes) carry no status and stay
  // eligible; campaign records must carry a launch-bound status.
  if (!statuses.length) return true;
  return statuses.some((status) => DASHBOARD_LAUNCH_BOUND_CAMPAIGN_STATUSES.has(status));
}

function hasDashboardDraftCampaignData(summary = null, selectedAdvertiserRecord = null) {
  return collectDashboardCampaignCandidates(summary, selectedAdvertiserRecord).some(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      !isDashboardReportingOnlyCampaign(candidate) &&
      !isDashboardLaunchBoundCampaignCandidate(candidate)
  );
}

function collectDashboardCampaignCandidates(summary = null, selectedAdvertiserRecord = null) {
  const candidates = [];
  const addCandidate = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(addCandidate);
      return;
    }
    candidates.push(value);
  };

  addCandidate(summary?.canonicalSummary?.flightWindow);
  addCandidate(summary?.canonicalReporting?.flightWindow);
  addCandidate(summary?.canonical?.flightWindow);
  addCandidate(summary?.campaign?.flightWindow);
  addCandidate(summary?.campaign);
  addCandidate(summary?.activeCampaign);
  addCandidate(summary?.currentCampaign);
  addCandidate(summary?.selectedCampaign);
  addCandidate(summary?.campaigns);
  addCandidate(summary?.runningCampaigns);
  addCandidate(summary?.activeCampaigns);
  addCandidate(summary?.upcomingCampaign);
  addCandidate(summary?.upcomingCampaigns);
  addCandidate(summary?.completedCampaign);
  addCandidate(summary?.completedCampaigns);
  addCandidate(summary?.canonicalSummary?.campaignScope?.activeCampaigns);
  addCandidate(summary?.canonicalReporting?.campaignScope?.activeCampaigns);
  addCandidate(summary?.metrics?.flightWindow);
  addCandidate(summary?.metrics?.campaignFlightWindow);
  addCandidate(selectedAdvertiserRecord?.campaign);
  addCandidate(selectedAdvertiserRecord?.activeCampaign);
  addCandidate(selectedAdvertiserRecord?.campaigns);
  addCandidate(selectedAdvertiserRecord?.upcomingCampaign);
  addCandidate(selectedAdvertiserRecord?.upcomingCampaigns);
  addCandidate(selectedAdvertiserRecord?.completedCampaign);
  addCandidate(selectedAdvertiserRecord?.completedCampaigns);
  addCandidate(selectedAdvertiserRecord?.campaignFlightWindow);
  addCandidate(selectedAdvertiserRecord?.flightWindow);

  return candidates;
}

export function resolveDashboardCampaignWindow(summary = null, selectedAdvertiserRecord = null) {
  const candidates = collectDashboardCampaignCandidates(summary, selectedAdvertiserRecord).filter(
    isDashboardLaunchBoundCampaignCandidate
  );
  const windows = candidates
    .map(extractCampaignWindow)
    .filter((window) => window?.start && window?.end);

  if (!windows.length) return null;

  const starts = windows.map((window) => window.start).sort();
  const ends = windows.map((window) => window.end).sort();
  return {
    start: starts[0],
    end: ends[ends.length - 1],
  };
}

function hasDashboardCampaignData(summary = null, selectedAdvertiserRecord = null) {
  return collectDashboardCampaignCandidates(summary, selectedAdvertiserRecord).length > 0;
}

// Advertiser-level approved flight rollup (canonicalSummary.advertiserApprovedFlight).
// The backend (advertiserFlightRollupService) is the ONLY authority on the
// union/gap arithmetic; this resolver renders the contract verbatim and
// performs no date-union math. When the field is absent (older backend) it
// returns null and the locally-derived campaign window keeps today's behavior.
export function resolveDashboardAdvertiserApprovedFlight(summary = null) {
  const rollup = summary?.canonicalSummary?.advertiserApprovedFlight || null;
  if (!rollup || typeof rollup !== "object") return null;
  const start = toDashboardDateKey(rollup.aggregateStartDate);
  const end = toDashboardDateKey(rollup.aggregateEndDate);
  if (!start || !end) return null;
  return {
    start,
    end,
    continuous: rollup.continuous === true,
    segments: Array.isArray(rollup.segments) ? rollup.segments : [],
    gaps: Array.isArray(rollup.gaps) ? rollup.gaps : [],
    contributingCampaignCount: Number(rollup.contributingCampaignCount) || 0,
  };
}

function formatDashboardFlightSegmentsDetail(segments = []) {
  return segments
    .map((segment) => {
      const start = formatDashboardShortDate(segment?.start);
      const end = formatDashboardShortDate(segment?.end);
      if (!start || !end) return "";
      return start === end ? start : `${start}–${end}`;
    })
    .filter(Boolean)
    .join(" · ");
}

function formatDashboardFlightBreakNote(gaps = []) {
  return gaps
    .map((gap) => {
      const start = formatDashboardShortDate(gap?.start);
      const end = formatDashboardShortDate(gap?.end);
      if (!start || !end) return "";
      const between = dashboardDaysBetween(gap.start, gap.end);
      const dayCount = Number.isFinite(between) ? between + 1 : null;
      const range = start === end ? start : `${start}–${end}`;
      return dayCount ? `${dayCount}-DAY BREAK · ${range}` : `BREAK · ${range}`;
    })
    .filter(Boolean)
    .join(" · ");
}

// Presentation-only decoration of the countdown model from the backend rollup:
// segmented ranges when the flight has real gaps, the break note, and the
// unobtrusive multi-campaign count. No dates are derived here.
function decorateDashboardFlightModel(model, approvedFlight) {
  if (!approvedFlight) return model;
  const decorated = { ...model };
  if (approvedFlight.gaps.length > 0) {
    const segmentsDetail = formatDashboardFlightSegmentsDetail(approvedFlight.segments);
    const breakNote = formatDashboardFlightBreakNote(approvedFlight.gaps);
    if (segmentsDetail) decorated.detail = segmentsDetail;
    if (breakNote) decorated.flightBreakNote = breakNote;
  }
  if (approvedFlight.contributingCampaignCount > 1) {
    decorated.campaignCountNote = `${approvedFlight.contributingCampaignCount} campaigns`;
  }
  return decorated;
}

function getDashboardCampaignLifecycleState(window = null, todayDateKey = "") {
  const today = toDashboardDateKey(todayDateKey);
  if (!today || !window?.end) return "missing_dates";

  const startsIn = window.start ? dashboardDaysBetween(today, window.start) : null;
  if (Number.isFinite(startsIn) && startsIn > 0) return "upcoming";

  const daysLeft = dashboardDaysBetween(today, window.end);
  if (!Number.isFinite(daysLeft)) return "missing_dates";
  if (daysLeft < 0) return "completed";
  return "active";
}

function selectDashboardCampaignLifecycle(summary = null, selectedAdvertiserRecord = null, todayDateKey = "") {
  const today = toDashboardDateKey(todayDateKey);
  const candidates = collectDashboardCampaignCandidates(summary, selectedAdvertiserRecord).filter(
    isDashboardLaunchBoundCampaignCandidate
  );
  const ranked = candidates.map((candidate, index) => {
    const window = extractCampaignWindow(candidate);
    const state = getDashboardCampaignLifecycleState(window, today);
    return {
      candidate,
      index,
      state,
      window,
      daysUntilStart: window?.start ? dashboardDaysBetween(today, window.start) : null,
      daysUntilEnd: window?.end ? dashboardDaysBetween(today, window.end) : null,
    };
  });

  const active = ranked
    .filter((item) => item.state === "active")
    .sort((a, b) => (a.daysUntilEnd ?? Infinity) - (b.daysUntilEnd ?? Infinity));
  if (active.length) return active[0];

  const upcoming = ranked
    .filter((item) => item.state === "upcoming")
    .sort((a, b) => (a.daysUntilStart ?? Infinity) - (b.daysUntilStart ?? Infinity));
  if (upcoming.length) return upcoming[0];

  const completed = ranked
    .filter((item) => item.state === "completed")
    .sort((a, b) => (b.daysUntilEnd ?? -Infinity) - (a.daysUntilEnd ?? -Infinity));
  if (completed.length) return completed[0];

  return ranked.find((item) => item.state === "missing_dates") || null;
}

function getDashboardDurationValue(days, targetRaw = null, now = null) {
  const numericDays = Number(days);
  const nowDate = toDashboardDate(now);
  const targetDate = toDashboardDate(targetRaw);
  if (
    nowDate &&
    targetDate &&
    targetDate.getTime() > nowDate.getTime() &&
    Number.isFinite(numericDays) &&
    numericDays <= 1
  ) {
    const hours = Math.ceil((targetDate.getTime() - nowDate.getTime()) / DASHBOARD_HOUR_MS);
    if (Number.isFinite(hours) && hours > 0 && hours < 48) {
      return `${hours} ${hours === 1 ? "HR" : "HRS"}`;
    }
  }

  if (!Number.isFinite(numericDays)) return "--";
  if (numericDays <= 0) return "TODAY";
  return `${numericDays} ${numericDays === 1 ? "DAY" : "DAYS"}`;
}

export function resolveDashboardEdenToplineTimeframe(
  summary = null,
  selectedAdvertiserRecord = null,
  dashboardEdenContext = null
) {
  const campaignWindow = resolveDashboardCampaignWindow(summary, selectedAdvertiserRecord);
  if (campaignWindow?.start && campaignWindow?.end) return campaignWindow;

  const reportingWindow =
    extractCampaignWindow(summary?.canonicalSummary?.timeframe) ||
    extractCampaignWindow(summary?.canonicalReporting?.timeframe) ||
    extractCampaignWindow(summary?.canonical?.timeframe) ||
    extractCampaignWindow(summary?.reportingWindow) ||
    extractCampaignWindow(summary?.metrics?.spendToDateWindow) ||
    extractCampaignWindow({
      start: dashboardEdenContext?.campaignTiming?.startDate,
      end: dashboardEdenContext?.campaignTiming?.endDate,
    });

  return reportingWindow?.start && reportingWindow?.end
    ? { start: reportingWindow.start, end: reportingWindow.end }
    : null;
}

// Platform delivery liveness for the campaign countdown card. A launch-bound
// campaign with a live-dated flight window is only "live" when a platform is
// actually delivering (platformsRunning > 0). Missing/zero counts fail closed
// to not-live: the card must never claim an active countdown for a campaign
// with no delivery (2026-07-06 Quantum incident — an internal authorization
// row with a 30-day window but zero platforms live rendered as an active
// "30 DAYS" campaign).
function resolveDashboardPlatformsLiveCount(summary = null) {
  // One derivation, shared with the welcome metric card: the countdown card's
  // "is anything actually delivering?" question and the "Platforms Live" tile
  // are the same question, and they may never answer it differently.
  return getWelcomeDisplayPlatformsLive(summary);
}

export function getCampaignDaysRemainingModel({
  summary = null,
  selectedAdvertiserRecord = null,
  todayDateKey = getDashboardLocalDateKey(getDashboardTimeZone()),
  now = null,
} = {}) {
  const today = toDashboardDateKey(todayDateKey);
  const lifecycle = selectDashboardCampaignLifecycle(summary, selectedAdvertiserRecord, today);
  // The backend advertiser rollup, when present, is the authority for the
  // displayed advertiser-level flight window; the locally-derived campaign
  // window remains the fallback for older backends.
  const approvedFlight = resolveDashboardAdvertiserApprovedFlight(summary);
  const window = approvedFlight
    ? {
        start: approvedFlight.start,
        end: approvedFlight.end,
        startRaw: approvedFlight.start,
        endRaw: approvedFlight.end,
      }
    : lifecycle?.window || null;
  const lifecycleState = approvedFlight
    ? getDashboardCampaignLifecycleState(window, today)
    : lifecycle?.state || null;

  if (!today) {
    return EMPTY_CAMPAIGN_DAYS_MODEL;
  }

  if (!lifecycle && !approvedFlight) {
    // Draft/unapproved campaign data stays neutral; reporting-only imported
    // campaigns and truly empty advertisers keep the empty state.
    return hasDashboardDraftCampaignData(summary, selectedAdvertiserRecord)
      ? MISSING_CAMPAIGN_DATES_MODEL
      : EMPTY_CAMPAIGN_DAYS_MODEL;
  }

  if (lifecycleState === "missing_dates") {
    return hasDashboardCampaignData(summary, selectedAdvertiserRecord)
      ? MISSING_CAMPAIGN_DATES_MODEL
      : EMPTY_CAMPAIGN_DAYS_MODEL;
  }

  if (lifecycleState === "upcoming") {
    const startsIn = window.start ? dashboardDaysBetween(today, window.start) : null;
    return decorateDashboardFlightModel(
      {
        value: getDashboardDurationValue(startsIn, window.startRaw, now),
        label: "Launch Countdown",
        detail: `Flight Begins ${formatDashboardShortDate(window.start)}`,
        state: "upcoming",
      },
      approvedFlight
    );
  }

  if (lifecycleState === "completed") {
    return decorateDashboardFlightModel(
      {
        value: "DONE",
        label: "Campaign Complete",
        detail: `Flight Ended ${formatDashboardShortDate(window.end)}`,
        state: "completed",
      },
      approvedFlight
    );
  }

  const daysLeft = dashboardDaysBetween(today, window.end);
  if (!Number.isFinite(daysLeft)) {
    return MISSING_CAMPAIGN_DATES_MODEL;
  }

  const durationValue = getDashboardDurationValue(Math.max(0, daysLeft), window.endRaw, now);
  const platformsLive = resolveDashboardPlatformsLiveCount(summary);
  const flightEndsDetail = `Flight Ends ${formatDashboardShortDate(window.end)}`;

  // Truly live: the flight window spans today AND a platform is delivering.
  if (platformsLive > 0) {
    return decorateDashboardFlightModel(
      {
        value: durationValue,
        label: "Days Remaining",
        detail: flightEndsDetail,
        state: "active",
        platformsLive,
      },
      approvedFlight
    );
  }

  // Approved with a committed live-dated window but nothing is delivering yet:
  // a PLANNED campaign, never presented as an active countdown.
  return decorateDashboardFlightModel(
    {
      value: durationValue,
      label: "Planned",
      detail: flightEndsDetail,
      state: "planned",
      platformsLive: 0,
    },
    approvedFlight
  );
}

function getWelcomeSummaryMetricSignature(summary) {
  if (summary?.meta?.metricSignature) return summary.meta.metricSignature;
  return buildAnalyticsMetricSignature({
    advertiserId: summary?.advertiser?.id || null,
    metrics: {
      spendToDate: summary?.metrics?.spendToDate ?? null,
      spendToDateLabel: summary?.metrics?.spendToDateLabel || "",
      spendToDateWindow: summary?.metrics?.spendToDateWindow || null,
      campaignsRunning: summary?.metrics?.campaignsRunning ?? null,
      platformsRunning: summary?.metrics?.platformsRunning ?? null,
      spendSourceTier: summary?.metrics?.spendSourceTier || null,
      spendBasis: summary?.metrics?.spendBasis || null,
      pricingBasis: summary?.metrics?.pricingBasis || null,
    },
    trend: Array.isArray(summary?.trend)
      ? summary.trend.map((point) => ({
          date: point?.date || null,
          value: point?.value ?? null,
          source: point?.source || null,
        }))
      : [],
  });
}

function getWelcomeSummarySourceFreshness(summary) {
  return summary?.meta?.sourceFreshness || summary?.meta?.campaignSignature || "";
}

function resolveBrandLogoUrl(logoUrl) {
  const raw = String(logoUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/api/")) return `${API_BASE_URL}${raw}`;
  if (raw.startsWith("/")) return raw;
  return "";
}

function preloadImage(url, timeoutMs = 4500) {
  return new Promise((resolve) => {
    if (!url) return resolve();

    const img = new Image();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      if (typeof img.decode === "function") {
        img.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };

    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };

    img.src = url;
  });
}

const DEFAULT_WEATHER_MODEL = Object.freeze({
  temperature: null,
  temperatureUnit: "F",
  condition: "Live conditions unavailable",
  wind: null,
  humidity: null,
  locationLabel: "Local feed pending",
  updatedAt: null,
  isLive: false,
  canRequestLocalWeather: false,
  actionLabel: "Enable Local Weather",
});
const WEATHER_ENABLED_KEY = "cphereWeatherEnabled";
const WEATHER_GEOLOCATION_OPTIONS = Object.freeze({
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: WEATHER_REFRESH_INTERVAL_MS,
});
const WEATHER_ENABLE_MODEL = Object.freeze({
  ...DEFAULT_WEATHER_MODEL,
  condition: "Live local weather available",
  locationLabel: "Permission required",
  canRequestLocalWeather: true,
});
const WEATHER_LOCATING_MODEL = Object.freeze({
  ...DEFAULT_WEATHER_MODEL,
  condition: "Locating local conditions...",
  locationLabel: "Resolving location",
});
const WEATHER_LOADING_MODEL = Object.freeze({
  ...DEFAULT_WEATHER_MODEL,
  condition: "Loading local weather...",
  locationLabel: "Current location",
});
const WEATHER_PERMISSION_DENIED_MODEL = Object.freeze({
  ...DEFAULT_WEATHER_MODEL,
  condition: "Location permission needed",
  locationLabel: "Live local weather unavailable",
  canRequestLocalWeather: true,
});
const WEATHER_LOCATION_UNAVAILABLE_MODEL = Object.freeze({
  ...DEFAULT_WEATHER_MODEL,
  condition: "Location unavailable",
  locationLabel: "Live local weather unavailable",
  canRequestLocalWeather: true,
});
const WEATHER_FETCH_FAILED_MODEL = Object.freeze({
  ...DEFAULT_WEATHER_MODEL,
  condition: "Weather service unavailable",
  locationLabel: "Live local weather unavailable",
  canRequestLocalWeather: true,
  actionLabel: "Retry Local Weather",
});

function hasStoredWeatherEnabled() {
  try {
    return localStorage.getItem(WEATHER_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}

function rememberWeatherEnabled() {
  try {
    localStorage.setItem(WEATHER_ENABLED_KEY, "true");
  } catch {}
}

function clearWeatherEnabled() {
  try {
    localStorage.removeItem(WEATHER_ENABLED_KEY);
  } catch {}
}

function formatWeatherUpdatedAt(updatedAt) {
  if (!updatedAt) return "Live feed pending";
  const parsed = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) return "Live feed pending";
  return parsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatWeatherTemperature(temperature) {
  const numeric = Number(temperature);
  if (!Number.isFinite(numeric)) return "--";
  return `${Math.round(numeric)}°`;
}

export function shouldLogDashboardWeatherDiagnostics() {
  return process.env.NODE_ENV !== "production";
}

function normalizeWeatherFetchSource(source, fallback = "unknown") {
  const normalized = typeof source === "string" ? source.trim() : "";
  return normalized || fallback;
}

export function buildDashboardWeatherDiagnostics(event, details = {}) {
  const latitude = Number(details.latitude ?? details.position?.coords?.latitude);
  const longitude = Number(details.longitude ?? details.position?.coords?.longitude);
  const providerErrors = Array.isArray(details.providerErrors)
    ? details.providerErrors
        .map((entry) => ({
          provider: String(entry?.provider || "").trim(),
          message: String(entry?.message || "").trim(),
        }))
        .filter((entry) => entry.provider || entry.message)
    : [];

  return {
    event: String(event || "weather_event").trim() || "weather_event",
    source: normalizeWeatherFetchSource(details.source),
    geolocationAvailable: details.geolocationAvailable === true,
    secureContext: details.secureContext !== false,
    permissionDenied: details.permissionDenied === true,
    latitudePresent: Number.isFinite(latitude),
    longitudePresent: Number.isFinite(longitude),
    endpointUrl: String(details.endpointUrl || "").trim() || "",
    responseStatus: Number.isFinite(Number(details.responseStatus))
      ? Number(details.responseStatus)
      : null,
    isLive: details.isLive === true,
    isStale: details.isStale === true,
    provider: String(details.provider || "").trim() || null,
    errorCode: String(details.errorCode || details.error || "").trim() || null,
    errorMessage:
      String(details.errorMessage || details.condition || details.message || "").trim() || null,
    providerErrors,
  };
}

export function debugLogDashboardWeatherDiagnostics(entry) {
  if (!shouldLogDashboardWeatherDiagnostics()) return false;
  if (typeof console === "undefined" || typeof console.debug !== "function") return false;
  console.debug("[Dashboard][weather]", entry);
  return true;
}

export function normalizeDashboardWeatherPayload(payload, fallbackModel = WEATHER_FETCH_FAILED_MODEL) {
  if (!payload || typeof payload !== "object" || payload.isLive !== true) {
    return {
      ...fallbackModel,
      condition: String(payload?.condition || "").trim() || fallbackModel.condition,
      locationLabel: String(payload?.locationLabel || "").trim() || fallbackModel.locationLabel,
      error: payload?.error || fallbackModel.error || null,
    };
  }

  const temperature = Number(payload.temperature);

  return {
    ...DEFAULT_WEATHER_MODEL,
    temperature: Number.isFinite(temperature) ? Math.round(temperature) : null,
    temperatureUnit: payload.temperatureUnit
      ? String(payload.temperatureUnit).toUpperCase()
      : DEFAULT_WEATHER_MODEL.temperatureUnit,
    condition: String(payload.condition || "").trim() || "Current Conditions",
    wind: payload.wind ? String(payload.wind) : null,
    humidity: payload.humidity ? String(payload.humidity) : null,
    locationLabel: String(payload.locationLabel || "").trim() || DEFAULT_WEATHER_MODEL.locationLabel,
    updatedAt: payload.updatedAt || null,
    isLive: true,
    canRequestLocalWeather: false,
  };
}

function buildLocalWeatherEndpoint(position) {
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";

  const query = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
  });

  return `${API_BASE_URL}/api/weather/current?${query.toString()}`;
}

function isBrowserGeolocationSecureContext() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext === true) return true;
  const hostname = String(window.location?.hostname || "").toLowerCase();
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

export function getDashboardWeatherModelForGeolocationError(error) {
  return Number(error?.code) === 1
    ? WEATHER_PERMISSION_DENIED_MODEL
    : WEATHER_LOCATION_UNAVAILABLE_MODEL;
}

export function requestDashboardLocalWeather({
  geolocation,
  secureContext = true,
  setWeatherModel,
  loadWeatherForPosition,
  rememberEnabled = rememberWeatherEnabled,
  clearEnabled = clearWeatherEnabled,
  isCurrentAttempt = () => true,
  startRefresh,
  logDiagnostics,
  source = "unknown",
} = {}) {
  const requestSource = normalizeWeatherFetchSource(source);
  const canApplyState = () =>
    typeof isCurrentAttempt !== "function" || isCurrentAttempt() !== false;
  const applyWeatherModel = (model) => {
    if (canApplyState() && typeof setWeatherModel === "function") {
      setWeatherModel(model);
    }
  };
  const emitDiagnostic = (event, details = {}) => {
    if (typeof logDiagnostics !== "function") return;
    logDiagnostics(buildDashboardWeatherDiagnostics(event, details));
  };

  emitDiagnostic("geolocation_requested", {
    source: requestSource,
    geolocationAvailable: typeof geolocation?.getCurrentPosition === "function",
    secureContext,
  });

  if (!secureContext || typeof geolocation?.getCurrentPosition !== "function") {
    clearEnabled();
    applyWeatherModel(WEATHER_LOCATION_UNAVAILABLE_MODEL);
    emitDiagnostic("geolocation_unavailable", {
      source: requestSource,
      geolocationAvailable: typeof geolocation?.getCurrentPosition === "function",
      secureContext,
      errorCode: "location_unavailable",
      errorMessage: WEATHER_LOCATION_UNAVAILABLE_MODEL.condition,
    });
    return false;
  }

  applyWeatherModel(WEATHER_LOCATING_MODEL);
  geolocation.getCurrentPosition(
    (position) => {
      if (!canApplyState()) return;
      emitDiagnostic("geolocation_succeeded", {
        source: requestSource,
        geolocationAvailable: true,
        secureContext,
        position,
      });
      if (!buildLocalWeatherEndpoint(position)) {
        clearEnabled();
        applyWeatherModel(WEATHER_LOCATION_UNAVAILABLE_MODEL);
        emitDiagnostic("geolocation_missing_coordinates", {
          source: requestSource,
          geolocationAvailable: true,
          secureContext,
          position,
          errorCode: "invalid_coordinates",
          errorMessage: WEATHER_LOCATION_UNAVAILABLE_MODEL.condition,
        });
        return;
      }

      rememberEnabled();
      if (typeof loadWeatherForPosition === "function") {
        loadWeatherForPosition(position, requestSource);
      }
      if (typeof startRefresh === "function") {
        startRefresh(position);
      }
    },
    (error) => {
      if (!canApplyState()) return;
      clearEnabled();
      applyWeatherModel(getDashboardWeatherModelForGeolocationError(error));
      emitDiagnostic("geolocation_failed", {
        source: requestSource,
        geolocationAvailable: true,
        secureContext,
        permissionDenied: Number(error?.code) === 1,
        errorCode: Number(error?.code) === 1 ? "location_permission_denied" : "location_unavailable",
        errorMessage: String(error?.message || "").trim() || null,
      });
    },
    WEATHER_GEOLOCATION_OPTIONS
  );
  return true;
}

function openSlackLauncher() {
  const fallbackTimer = window.setTimeout(() => {
    const pageVisible = typeof document === "undefined" || document.visibilityState !== "hidden";
    const pageFocused =
      typeof document === "undefined" ||
      typeof document.hasFocus !== "function" ||
      document.hasFocus();
    if (pageVisible && pageFocused) {
      window.location.href = SLACK_WEB_FALLBACK_URL;
    }
  }, SLACK_FALLBACK_DELAY_MS);

  const cancelFallbackOnAppOpen = () => {
    if (document.visibilityState === "hidden") {
      window.clearTimeout(fallbackTimer);
    }
  };

  document.addEventListener("visibilitychange", cancelFallbackOnAppOpen, { once: true });
  window.location.href = SLACK_DEEP_LINK;
}

function DashboardDateWidget() {
  const dateParts = formatDashboardDateParts();

  return (
    <aside className="dashboard-date-widget" aria-label="Current local date">
      <div className="dashboard-utility-orbit" aria-hidden="true" />
      <div className="dashboard-utility-kicker">Date</div>
      <div className="dashboard-date-day">{dateParts.dayNumber}</div>
      <div className="dashboard-date-monthday">{dateParts.monthDay}</div>
      <div className="dashboard-date-weekday">{dateParts.weekday}</div>
      {dateParts.year ? <div className="dashboard-date-year">{dateParts.year}</div> : null}
    </aside>
  );
}

function DashboardCampaignDaysWidget({ campaignDays = EMPTY_CAMPAIGN_DAYS_MODEL } = {}) {
  const model = {
    ...EMPTY_CAMPAIGN_DAYS_MODEL,
    ...(campaignDays && typeof campaignDays === "object" ? campaignDays : {}),
  };

  return (
    <aside
      className="dashboard-campaign-days-widget"
      aria-label="Campaign days remaining"
      data-campaign-countdown-state={model.state || "empty"}
    >
      <div className="dashboard-utility-orbit" aria-hidden="true" />
      <div className="dashboard-utility-kicker">Campaign</div>
      <div className="dashboard-campaign-days-number">{model.value || "--"}</div>
      <div className="dashboard-campaign-days-label">{model.label || "No Active Campaign"}</div>
      {model.detail ? <div className="dashboard-campaign-days-detail">{model.detail}</div> : null}
      {model.flightBreakNote ? (
        <div className="dashboard-campaign-days-detail dashboard-campaign-days-break">
          {model.flightBreakNote}
        </div>
      ) : null}
      {model.campaignCountNote ? (
        <div className="dashboard-campaign-days-detail dashboard-campaign-days-campaigns">
          {model.campaignCountNote}
        </div>
      ) : null}
    </aside>
  );
}

function DashboardSlackLauncher() {
  return (
    <button
      type="button"
      className="dashboard-slack-launcher"
      aria-label="Open Slack"
      title="Open Slack"
      onClick={openSlackLauncher}
    >
      <span className="dashboard-slack-label">Slack</span>
      <img
        src={slackImage}
        alt=""
        className="dashboard-slack-icon"
        aria-hidden="true"
        draggable={false}
      />
    </button>
  );
}

export function MusicCarousel() {
  return (
    <aside className="music-carousel-widget" aria-label="Music service shortcuts">
      <div className="music-carousel-orbit" aria-hidden="true" />
      <div className="music-carousel-label">Music</div>
      <div className="music-carousel-track">
        {MUSIC_SERVICE_LINKS.map((service) => (
          <a
            key={service.label}
            className="music-carousel-item"
            href={service.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${service.label}`}
            title={service.label}
          >
            <img
              src={service.logo}
              alt=""
              className="music-carousel-logo"
              aria-hidden="true"
              draggable={false}
            />
            <span className="music-carousel-name">{service.label}</span>
          </a>
        ))}
      </div>
    </aside>
  );
}

export function DashboardWeatherWidget({
  weather = DEFAULT_WEATHER_MODEL,
  temperature,
  temperatureUnit,
  condition,
  wind,
  humidity,
  locationLabel,
  updatedAt,
  isLive,
  onEnableLocalWeather,
} = {}) {
  const weatherData = {
    ...DEFAULT_WEATHER_MODEL,
    ...(weather && typeof weather === "object" ? weather : {}),
    ...(temperature != null ? { temperature } : {}),
    ...(temperatureUnit != null ? { temperatureUnit } : {}),
    ...(condition != null ? { condition } : {}),
    ...(wind != null ? { wind } : {}),
    ...(humidity != null ? { humidity } : {}),
    ...(locationLabel != null ? { locationLabel } : {}),
    ...(updatedAt != null ? { updatedAt } : {}),
    ...(isLive != null ? { isLive } : {}),
  };
  const live = weatherData.isLive === true;
  const displayTemperature = live
    ? formatWeatherTemperature(weatherData.temperature)
    : "--";
  const displayCondition = weatherData.condition || "Live conditions unavailable";
  const displayLocation = weatherData.locationLabel || "Local feed pending";
  const displayWind = live && weatherData.wind ? weatherData.wind : "Pending";
  const displayHumidity = live && weatherData.humidity ? weatherData.humidity : "Pending";
  const displayUpdatedAt = live ? formatWeatherUpdatedAt(weatherData.updatedAt) : "Live feed pending";
  const showEnableButton =
    !live &&
    weatherData.canRequestLocalWeather === true &&
    typeof onEnableLocalWeather === "function";
  const enableButtonLabel = weatherData.actionLabel || "Enable Local Weather";

  return (
    <aside
      className="dashboard-weather-widget"
      aria-label={live ? "Live local weather conditions" : "Weather feed pending"}
      data-weather-live={live ? "true" : "false"}
    >
      <div className="dashboard-weather-orbit" aria-hidden="true" />
      <div className="dashboard-weather-kicker">Weather</div>
      <div className="dashboard-weather-temperature">{displayTemperature}</div>
      <div className="dashboard-weather-condition">{displayCondition}</div>
      {showEnableButton ? (
        <button
          type="button"
          className="dashboard-weather-enable"
          onClick={onEnableLocalWeather}
        >
          {enableButtonLabel}
        </button>
      ) : null}
      <div className="dashboard-weather-divider" />
      <div className="dashboard-weather-row">
        <span>Location</span>
        <strong>{displayLocation}</strong>
      </div>
      <div className="dashboard-weather-row">
        <span>Wind</span>
        <strong>{displayWind}</strong>
      </div>
      <div className="dashboard-weather-row">
        <span>Humidity</span>
        <strong>{displayHumidity}</strong>
      </div>
      <div className="dashboard-weather-row">
        <span>Updated</span>
        <strong>{displayUpdatedAt}</strong>
      </div>
    </aside>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { search: dashboardSearch } = useLocation();
  const reportingFilters = new URLSearchParams(dashboardSearch);
  const reportingStartDate = reportingFilters.get("startDate") || null;
  const reportingEndDate = reportingFilters.get("endDate") || null;
  const { selectedAdvertiser, selectAdvertiser } = useAdvertiser();

  // ✅ SINGLE-OWNER logout
  const {
    logout,
    authToken: contextAuthToken,
    email: contextEmail,
    user: contextUser,
    role: contextRole,
  } = useAuth() || {};

  const [allAdvertisers, setAllAdvertisers] = useState([]);
  const [accessIds, setAccessIds] = useState([]);

  const [catalogReady, setCatalogReady] = useState(false);
  const [accessReady, setAccessReady] = useState(false);

  const [assetMap, setAssetMap] = useState(() => {
    return (
      CORTEX_ASSET_MAP_WARM || {
        cphereHeaderLogo,
        cphereSocialImage,
        integrationsImage,
        genesisImage,
        logoutImage,
      }
    );
  });

  const [assetsReady, setAssetsReady] = useState(CORTEX_ASSETS_WARM);

  const [fontsReady, setFontsReady] = useState(() => {
    try {
      return typeof document !== "undefined" &&
        document.fonts &&
        document.fonts.status === "loaded"
        ? true
        : false;
    } catch {
      return true;
    }
  });

  const [fadeIn, setFadeIn] = useState(false);
  const [showLoaderOverlay, setShowLoaderOverlay] = useState(true);
  const [fadeOutLoader, setFadeOutLoader] = useState(false);
  const [loaderFailsafeReleased, setLoaderFailsafeReleased] = useState(false);
  const [failedWelcomeBrandLogos, setFailedWelcomeBrandLogos] = useState([]);
  const [welcomeCurrentTime, setWelcomeCurrentTime] = useState(() => new Date());
  const [advertiserSelectorOpen, setAdvertiserSelectorOpen] = useState(false);
  const [weatherModel, setWeatherModel] = useState(() =>
    hasStoredWeatherEnabled() ? WEATHER_LOCATING_MODEL : WEATHER_ENABLE_MODEL
  );
  const weatherControllerRef = useRef(null);
  const weatherRefreshIntervalRef = useRef(null);
  const weatherMountedRef = useRef(true);
  const weatherRequestIdRef = useRef(0);
  const weatherLocationAttemptIdRef = useRef(0);
  const weatherAutoRefreshAttemptedRef = useRef(false);

  const clearWeatherRequest = useCallback(() => {
    weatherRequestIdRef.current += 1;
    if (weatherControllerRef.current) {
      weatherControllerRef.current.abort();
      weatherControllerRef.current = null;
    }
    if (weatherRefreshIntervalRef.current) {
      window.clearInterval(weatherRefreshIntervalRef.current);
      weatherRefreshIntervalRef.current = null;
    }
  }, []);

  const loadWeatherForPosition = useCallback(async (position, source = "unknown") => {
    const requestSource = normalizeWeatherFetchSource(source);
    const requestId = weatherRequestIdRef.current + 1;
    weatherRequestIdRef.current = requestId;
    const isCurrentWeatherRequest = () =>
      weatherMountedRef.current && weatherRequestIdRef.current === requestId;
    const endpoint = buildLocalWeatherEndpoint(position);
    const emitWeatherDiagnostic = (event, details = {}) => {
      debugLogDashboardWeatherDiagnostics(
        buildDashboardWeatherDiagnostics(event, {
          geolocationAvailable: true,
          secureContext: true,
          position,
          endpointUrl: endpoint,
          source: requestSource,
          ...details,
        })
      );
    };
    if (!endpoint) {
      clearWeatherEnabled();
      emitWeatherDiagnostic("weather_request_skipped", {
        errorCode: "invalid_coordinates",
        errorMessage: WEATHER_LOCATION_UNAVAILABLE_MODEL.condition,
      });
      if (isCurrentWeatherRequest()) setWeatherModel(WEATHER_LOCATION_UNAVAILABLE_MODEL);
      return;
    }

    if (weatherControllerRef.current) weatherControllerRef.current.abort();
    weatherControllerRef.current = new AbortController();
    setWeatherModel(WEATHER_LOADING_MODEL);
    emitWeatherDiagnostic("weather_request_started");

    try {
      const res = await fetchWithDashboardTimeout(
        endpoint,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          cache: "no-store",
          signal: weatherControllerRef.current.signal,
        },
        8000
      );
      if (!res.ok) throw new Error(`Failed to load weather (${res.status})`);
      const payload = await res.json();
      emitWeatherDiagnostic("weather_response_received", {
        responseStatus: res.status,
        isLive: payload?.isLive === true,
        isStale: payload?.isStale === true,
        provider: payload?.provider || null,
        errorCode: payload?.error || null,
        errorMessage: payload?.condition || null,
        providerErrors: payload?.providerErrors,
      });
      if (isCurrentWeatherRequest()) {
        setWeatherModel(normalizeDashboardWeatherPayload(payload));
      }
    } catch (error) {
      if (isCurrentWeatherRequest() && !isAbortLikeError(error)) {
        console.warn("[Dashboard] Weather feed unavailable:", error);
        emitWeatherDiagnostic("weather_request_failed", {
          errorCode: "weather_fetch_failed",
          errorMessage: String(error?.message || "").trim() || "Weather request failed.",
        });
        setWeatherModel(WEATHER_FETCH_FAILED_MODEL);
      }
    }
  }, []);

  const handleEnableLocalWeather = useCallback((source = "retry") => {
    const requestSource = normalizeWeatherFetchSource(source, "retry");
    clearWeatherRequest();
    const locationAttemptId = weatherLocationAttemptIdRef.current + 1;
    weatherLocationAttemptIdRef.current = locationAttemptId;
    const isCurrentLocationAttempt = () =>
      weatherMountedRef.current && weatherLocationAttemptIdRef.current === locationAttemptId;

    const geolocation =
      typeof navigator !== "undefined" && navigator.geolocation
        ? navigator.geolocation
        : null;

    const isClearlyInsecureHttp =
      typeof window !== "undefined" &&
      window.location?.protocol === "http:" &&
      !isBrowserGeolocationSecureContext();

    requestDashboardLocalWeather({
      geolocation,
      secureContext: !isClearlyInsecureHttp,
      setWeatherModel,
      loadWeatherForPosition,
      rememberEnabled: rememberWeatherEnabled,
      clearEnabled: clearWeatherEnabled,
      isCurrentAttempt: isCurrentLocationAttempt,
      startRefresh: (position) => {
        weatherRefreshIntervalRef.current = window.setInterval(
          () => loadWeatherForPosition(position, "refresh"),
          WEATHER_REFRESH_INTERVAL_MS
        );
      },
      logDiagnostics: debugLogDashboardWeatherDiagnostics,
      source: requestSource,
    });
  }, [clearWeatherRequest, loadWeatherForPosition]);

  useEffect(() => {
    if (weatherAutoRefreshAttemptedRef.current || !hasStoredWeatherEnabled()) return;
    weatherAutoRefreshAttemptedRef.current = true;
    handleEnableLocalWeather("dashboard-load");
  }, [handleEnableLocalWeather]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setWelcomeCurrentTime(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(DASH_READY_KEY, "false");
    } catch {}
  }, []);

  // ✅ Preload dashboard assets
  useEffect(() => {
    let cancelled = false;

    if (CORTEX_ASSETS_WARM && CORTEX_ASSET_MAP_WARM) {
      setAssetMap(CORTEX_ASSET_MAP_WARM);
      setAssetsReady(true);
      return;
    }

    const assets = {
      cphereHeaderLogo,
      cphereSocialImage,
      integrationsImage,
      genesisImage,
      logoutImage,
    };

    (async () => {
      try {
        await Promise.all(
          [...Object.values(assets), ...getVerticalDashboardImagePreloadUrls()].map((u) =>
            preloadImage(u)
          )
        );
      } finally {
        if (cancelled) return;
        CORTEX_ASSETS_WARM = true;
        CORTEX_ASSET_MAP_WARM = assets;
        setAssetMap(assets);
        setAssetsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ✅ Wait for fonts exactly like superadmin
  useEffect(() => {
    let cancelled = false;

    try {
      const fonts = document?.fonts;

      if (!fonts?.ready) {
        setFontsReady(true);
        return;
      }

      fonts.ready
        .then(() => {
          if (!cancelled) setFontsReady(true);
        })
        .catch(() => {
          if (!cancelled) setFontsReady(true);
        });
    } catch {
      setFontsReady(true);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const getToken = useCallback(
    () =>
      contextAuthToken ||
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("authToken") ||
      "",
    [contextAuthToken]
  );

  const getEmail = useCallback(
    () =>
      contextEmail ||
      localStorage.getItem("userEmail") ||
      localStorage.getItem("email") ||
      "",
    [contextEmail]
  );

  const normalizeList = (data) => {
    if (!Array.isArray(data)) return [];
    return data
      .map((a) => ({
        id: a.id ?? a._id ?? a.advertiserId ?? a.name,
        vertical: normalizeAdvertiserVertical(a.vertical),
        raw: a,
        name:
          a.name ??
          a.advertiser ??
          String(a.id ?? a._id ?? a.advertiserId ?? a.name),
      }))
      .filter((x) => x.id !== undefined && x.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const persistSelectionByName = useCallback((name) => {
    const hit = allAdvertisers.find((a) => a.name === name);
    if (hit) {
      localStorage.setItem(KEY_NAME, hit.name);
      localStorage.setItem(KEY_ID, String(hit.id));
    } else {
      localStorage.removeItem(KEY_NAME);
      localStorage.removeItem(KEY_ID);
    }
  }, [allAdvertisers]);

  const refreshAccess = useCallback(async (signal) => {
    const token = getToken();
    if (!token) {
      setAccessReady(true);
      return;
    }

    try {
      const endpoint = `${API_BASE_URL}/api/auth/me?ts=${Date.now()}`;
      const res = await fetchWithDashboardTimeout(
        endpoint,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
          signal,
        }
      );

      if (signal?.aborted) return;

      if (!res.ok) {
        setAccessReady(true);
        return;
      }

      const me = await res.json();

      const advIds =
        (Array.isArray(me.advertisers) && me.advertisers.length
          ? me.advertisers
          : me.advertiser_ids) || [];

      const ids = advIds.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
      setAccessIds(ids);

      const prev = JSON.parse(localStorage.getItem("authState") || "{}");
      localStorage.setItem(
        "authState",
        JSON.stringify({ ...prev, user: me, advertisers: ids })
      );
    } catch (e) {
      if (!isAbortLikeError(e)) {
        console.error("Failed to refresh /auth/me:", e);
      }
    } finally {
      if (!signal?.aborted) setAccessReady(true);
    }
  }, [getToken]);

  const fetchAdvertisers = useCallback(async (signal) => {
    const token = getToken();
    const email = getEmail();

    if (!token || !email) {
      setCatalogReady(true);
      return;
    }

    try {
      const res = await fetchWithDashboardTimeout(
        `${API_BASE_URL}/api/advertisers?email=${encodeURIComponent(
          email
        )}&ts=${Date.now()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
          signal,
        }
      );

      if (signal?.aborted) return;

      const data = await res.json();
      if (signal?.aborted) return;
      setAllAdvertisers(normalizeList(data));
    } catch (err) {
      if (!isAbortLikeError(err)) {
        console.error("Failed to fetch advertisers:", err);
      }
    } finally {
      if (!signal?.aborted) setCatalogReady(true);
    }
  }, [getEmail, getToken]);

  const visibleAdvertisers = useMemo(
    () => resolveDashboardVisibleAdvertisers(allAdvertisers, accessIds, contextRole || "client"),
    [allAdvertisers, accessIds, contextRole]
  );
  const handleDashboardAdvertiserSelect = useCallback(
    (advertiser) => {
      if (!advertiser) return;
      selectAdvertiser(advertiser);
      persistSelectionByName(advertiser.name);
      setAdvertiserSelectorOpen(false);
    },
    [persistSelectionByName, selectAdvertiser]
  );

  useEffect(() => {
    const controller = new AbortController();
    let focusController = null;
    fetchAdvertisers(controller.signal);
    refreshAccess(controller.signal);

    const onFocus = () => {
      if (focusController) focusController.abort();
      focusController = new AbortController();
      refreshAccess(focusController.signal);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      controller.abort();
      if (focusController) focusController.abort();
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchAdvertisers, refreshAccess]);

  useEffect(() => {
    if (!catalogReady || !accessReady) return;

    if (selectedAdvertiser) {
      const match = visibleAdvertisers.find(
        (a) =>
          a.name === selectedAdvertiser ||
          String(a.id) === String(selectedAdvertiser)
      );

      if (!match) {
        selectAdvertiser(null);
        localStorage.removeItem(KEY_NAME);
        localStorage.removeItem(KEY_ID);
      } else {
        selectAdvertiser(match);
        persistSelectionByName(match.name);
      }
      return;
    }

    const storedName = localStorage.getItem(KEY_NAME);
    if (storedName) {
      const storedMatch = visibleAdvertisers.find((a) => a.name === storedName);
      if (storedMatch) {
        selectAdvertiser(storedMatch);
        localStorage.setItem(KEY_ID, String(storedMatch.id));
        return;
      } else {
        localStorage.removeItem(KEY_NAME);
        localStorage.removeItem(KEY_ID);
      }
    }

    if (visibleAdvertisers.length === 1) {
      const only = visibleAdvertisers[0];
      selectAdvertiser(only);
      localStorage.setItem(KEY_NAME, only.name);
      localStorage.setItem(KEY_ID, String(only.id));
    }
  }, [
    catalogReady,
    accessReady,
    visibleAdvertisers,
    selectedAdvertiser,
    persistSelectionByName,
    selectAdvertiser,
  ]);

  // ✅ SINGLE owner logout
  const handleLogout = () => {
    try {
      if (typeof logout === "function") {
        logout();
        return;
      }
    } catch {}
    navigate("/login?logout=1", { replace: true });
  };

  const fullyReady = catalogReady && accessReady && assetsReady && fontsReady;
  const selectedAdvertiserRecord = useMemo(
    () =>
      visibleAdvertisers.find(
        (advertiser) =>
          advertiser.name === selectedAdvertiser ||
          String(advertiser.id) === String(selectedAdvertiser)
      ) || null,
    [selectedAdvertiser, visibleAdvertisers]
  );
  const selectedAdvertiserRecordId = Number(selectedAdvertiserRecord?.id);
  const reportingCampaignId = useDashboardCampaignFilter(dashboardSearch, selectedAdvertiserRecordId);
  const verticalDashboardConfig = useMemo(
    () => resolveVerticalDashboardConfigForAdvertiser(selectedAdvertiserRecord),
    [selectedAdvertiserRecord]
  );
  const verticalDashboardTileStyle = useMemo(
    () => ({
      backgroundImage: `url(${verticalDashboardConfig.tileImage})`,
      ...(verticalDashboardConfig.tileImageFit
        ? { backgroundSize: verticalDashboardConfig.tileImageFit }
        : {}),
      ...(verticalDashboardConfig.tileImagePosition
        ? { backgroundPosition: verticalDashboardConfig.tileImagePosition }
        : {}),
      ...(verticalDashboardConfig.tileImageRepeat
        ? { backgroundRepeat: verticalDashboardConfig.tileImageRepeat }
        : {}),
    }),
    [verticalDashboardConfig]
  );
  const dashboardAuthToken = getToken();
  const dashboardTimeZone = getDashboardTimeZone();
  const dashboardDateKey = getDashboardLocalDateKey(dashboardTimeZone);
  const dashboardAuthUserKey = String(
    contextUser?.id ||
      contextUser?._id ||
      contextEmail ||
      getEmail() ||
      "anonymous"
  );
  const welcomeSummaryRequestKey =
    dashboardAuthToken &&
    Number.isFinite(selectedAdvertiserRecordId) &&
    selectedAdvertiserRecordId > 0
      ? `${selectedAdvertiserRecordId}:${dashboardAuthUserKey}:${String(dashboardAuthToken).slice(-16)}:${dashboardDateKey}:${dashboardTimeZone}:${reportingCampaignId || "all"}:${reportingStartDate || ""}:${reportingEndDate || ""}`
      : "";
  const welcomeSummaryScopeKey = welcomeSummaryRequestKey
    ? buildAnalyticsScopeKey({
        panel: "dashboard-welcome",
        portal: "client",
        advertiserId: selectedAdvertiserRecordId,
        userKey: dashboardAuthUserKey,
        sessionKey: String(dashboardAuthToken).slice(-16),
        dateKey: dashboardDateKey,
        timeZone: dashboardTimeZone,
        campaignId: reportingCampaignId, startDate: reportingStartDate, endDate: reportingEndDate,
      })
    : "";
  const { summary: welcomeSummary, loading: welcomeSummaryLoading, error: welcomeSummaryError, retained: welcomeSummaryRetained,
    settledKey: welcomeSummarySettledKey } = useDashboardReporting({
    requestKey: welcomeSummaryRequestKey,
    advertiserId: selectedAdvertiserRecordId,
    timeZone: dashboardTimeZone,
    token: dashboardAuthToken,
    apiBase: API_BASE_URL,
    campaignId: reportingCampaignId, startDate: reportingStartDate, endDate: reportingEndDate,
    onSuccess: (payload) => writeCachedAnalyticsIntelligence(WELCOME_SUMMARY_CACHE_KEY, {
      scopeKey: welcomeSummaryScopeKey,
      metricSignature: getWelcomeSummaryMetricSignature(payload),
      sourceFreshness: getWelcomeSummarySourceFreshness(payload),
      payload: { summary: payload },
    }),
  });
  const activeWelcomeSummary =
    welcomeSummary?.__welcomeSummaryRequestKey === welcomeSummaryRequestKey
      ? welcomeSummary
      : null;
  const campaignsDeliveringTile = getWelcomeCampaignsDeliveringTile(activeWelcomeSummary);
  const campaignDaysModel = useMemo(
    () =>
      getCampaignDaysRemainingModel({
        summary: activeWelcomeSummary,
        selectedAdvertiserRecord,
        todayDateKey: dashboardDateKey,
      }),
    [activeWelcomeSummary, selectedAdvertiserRecord, dashboardDateKey]
  );
  const welcomeSummaryReady =
    !welcomeSummaryRequestKey || welcomeSummarySettledKey === welcomeSummaryRequestKey;
  // Keep advertiser summary refreshes local to the welcome panel; the full-screen
  // loader is only for the initial dashboard shell boot.
  const dashboardReady = fullyReady;
  const effectiveDashboardReady = dashboardReady || loaderFailsafeReleased;
  const dashboardEdenContext = useMemo(
    () =>
      buildEdenDashboardContext({
        summary: activeWelcomeSummary,
        selectedAdvertiserRecord,
        weather: weatherModel,
        campaignDays: campaignDaysModel,
        todayDateKey: dashboardDateKey,
      }),
    [
      activeWelcomeSummary,
      campaignDaysModel,
      dashboardDateKey,
      selectedAdvertiserRecord,
      weatherModel,
    ]
  );

  useEffect(() => {
    weatherMountedRef.current = true;
    return () => {
      weatherMountedRef.current = false;
      clearWeatherRequest();
    };
  }, [clearWeatherRequest]);

  const welcomeGreetingName = resolveDashboardViewerDisplayName(
    activeWelcomeSummary?.viewer,
    contextUser,
    getStoredDashboardViewerDisplayName()
  );
  const welcomeHeadline = useMemo(
    () => getTimeAwareWelcomeHeadline({ date: welcomeCurrentTime, name: welcomeGreetingName }),
    [welcomeCurrentTime, welcomeGreetingName]
  );
  const welcomeAdvertiserName =
    activeWelcomeSummary?.advertiser?.name || selectedAdvertiserRecord?.name || "";
  const dashboardEdenToplineMetrics = useMemo(
    () => buildDashboardEdenToplineMetrics(activeWelcomeSummary),
    [activeWelcomeSummary]
  );
  const dashboardEdenToplineInsight = useMemo(
    () => buildDashboardEdenToplineInsight(activeWelcomeSummary),
    [activeWelcomeSummary]
  );
  const dashboardEdenToplineAnalysis = useMemo(
    () => buildDashboardEdenToplineAnalysis(activeWelcomeSummary),
    [activeWelcomeSummary]
  );
  const dashboardEdenToplineTimeframe = useMemo(
    () =>
      resolveDashboardEdenToplineTimeframe(
        activeWelcomeSummary,
        selectedAdvertiserRecord,
        dashboardEdenContext
      ),
    [activeWelcomeSummary, dashboardEdenContext, selectedAdvertiserRecord]
  );
  const dashboardPageAwareness = useMemo(
    () => ({
      routeKey: "/dashboard",
      pageTitle: "Dashboard",
      pageType: "dashboard",
      advertiserId: selectedAdvertiserRecordId ?? null,
      advertiserName: welcomeAdvertiserName,
      timeframe: dashboardEdenToplineTimeframe,
      toplineScope: "aggregate",
      toplineAuthority: "shared_fetch",
      pageCapabilities: [
        "dashboard overview",
        "current campaign timing context",
        "local weather context",
        "live welcome summary",
      ],
      aggregateToplineMetrics: dashboardEdenToplineMetrics,
      metadata: {
        logoUrl:
          activeWelcomeSummary?.branding?.resolvedBrandLogoUrl ||
          activeWelcomeSummary?.advertiser?.logoUrl ||
          "",
        welcomeSummaryReady,
        dashboardContext: dashboardEdenContext,
        toplineInsight: dashboardEdenToplineInsight,
        toplineAnalysis: dashboardEdenToplineAnalysis,
      },
    }),
    [
      activeWelcomeSummary,
      dashboardEdenContext,
      dashboardEdenToplineAnalysis,
      dashboardEdenToplineInsight,
      dashboardEdenToplineMetrics,
      dashboardEdenToplineTimeframe,
      selectedAdvertiserRecordId,
      welcomeAdvertiserName,
      welcomeSummaryReady,
    ]
  );
  usePageAwareness(dashboardPageAwareness);
  const welcomeBrandLogos = useMemo(
    () =>
      resolveDashboardWelcomeBrandLogoCandidates({
        activeWelcomeSummary,
        selectedAdvertiserRecord,
      }),
    [activeWelcomeSummary, selectedAdvertiserRecord]
  );
  const {
    resolvedSrc: resolvedActiveWelcomeBrandLogo,
    handleError: handleWelcomeBrandLogoError,
  } = useResolvedImageCandidates(welcomeBrandLogos, { apiBase: API_BASE_URL });
  const fallbackWelcomeBrandLogo = cphereHeaderLogo;
  const welcomeBrandLogoSignature = useMemo(() => welcomeBrandLogos.join("\n"), [welcomeBrandLogos]);
  const welcomeBrandLogoSrc = resolveDashboardWelcomeBrandLogoSrc({
    candidates: welcomeBrandLogos,
    resolvedLogo: resolvedActiveWelcomeBrandLogo,
    fallbackLogo: fallbackWelcomeBrandLogo,
    failedLogos: failedWelcomeBrandLogos,
  });
  const welcomeCurrentTimeLabel = useMemo(
    () => formatDashboardCurrentTime(welcomeCurrentTime),
    [welcomeCurrentTime]
  );

  useEffect(() => {
    setFailedWelcomeBrandLogos([]);
  }, [welcomeBrandLogoSignature]);

  useEffect(() => {
    if (dashboardReady) return undefined;

    const timer = window.setTimeout(() => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[Dashboard][loader-state]", {
          event: "failsafe_release",
          catalogReady,
          accessReady,
          assetsReady,
          fontsReady,
        });
      }
      setLoaderFailsafeReleased(true);
      try {
        sessionStorage.setItem(DASH_READY_KEY, "true");
      } catch {}
    }, DASHBOARD_LOADER_FAILSAFE_MS);

    return () => window.clearTimeout(timer);
  }, [
    accessReady,
    assetsReady,
    catalogReady,
    dashboardReady,
    fontsReady,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    console.debug("[Dashboard][loader-state]", {
      catalogReady,
      accessReady,
      assetsReady,
      fontsReady,
      welcomeSummaryReady,
      welcomeSummaryLoading,
      dashboardReady,
      loaderFailsafeReleased,
      showLoaderOverlay,
    });
  }, [
    accessReady,
    assetsReady,
    catalogReady,
    dashboardReady,
    fontsReady,
    loaderFailsafeReleased,
    showLoaderOverlay,
    welcomeSummaryLoading,
    welcomeSummaryReady,
  ]);

  // ✅ Match superadmin behavior:
  // keep overlay up until assets + fonts + data are ready, then fade shell in and overlay out
  useEffect(() => {
    if (!effectiveDashboardReady) {
      setFadeIn(false);
      setFadeOutLoader(false);
      setShowLoaderOverlay(true);
      try {
        sessionStorage.setItem(DASH_READY_KEY, "false");
      } catch {}
      return;
    }

    let cancelled = false;

    const run = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      if (cancelled) return;

      setFadeIn(true);
      setFadeOutLoader(true);

      try {
        sessionStorage.setItem(DASH_READY_KEY, "true");
      } catch {}

      window.setTimeout(() => {
        if (!cancelled) setShowLoaderOverlay(false);
      }, 260);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [effectiveDashboardReady]);

  return (
    <div className="cortex-dashboard-page-container">
      <div className={`cortex-ready-shell ${fadeIn ? "cortex-fade-in" : ""}`}>
        <div className="cortex-dashboard cortex-page-intro-shell">
          <CphereHeaderLogo
            alt="Powered by Logo"
            className="cortex-poweredby-logo"
            onClick={() => navigate("/dashboard")}
            style={{ cursor: "pointer" }}
            draggable={false}
          />

          <div className="dashboard-left-utility-stack" aria-label="Dashboard left utility rail">
            <DashboardDateWidget />
            <DashboardWeatherWidget
              weather={weatherModel}
              onEnableLocalWeather={handleEnableLocalWeather}
            />
            <MusicCarousel />
          </div>
          <div className="dashboard-right-utility-stack" aria-label="Dashboard right utility rail">
            <DashboardCampaignDaysWidget campaignDays={campaignDaysModel} />
            <DashboardSlackLauncher />
          </div>

          <div className="cortex-hero-container">
            <div className="cortex-hero-pill">
              <span className="cortex-hero-pill-label cphere-hero-pill-wordmark">Cortex</span>
              <span className="cortex-hero-pill-tag cphere-hero-pill-tag">OS</span>
            </div>
            <div className="cortex-hero-shimmer-bar" />
          </div>

          <section
            className={`cortex-welcome-panel ${
              welcomeSummaryLoading ? "cortex-welcome-panel-loading" : ""
            }`}
            aria-label="Client welcome snapshot"
          >
            <div className="cortex-welcome-overview">
              <div className="cortex-welcome-hero-row">
                <div className="cortex-welcome-hero-shell" aria-hidden="true">
                  <div
                    className="cortex-welcome-hero-image"
                    style={{ backgroundImage: `url(${profileSettingsImage})` }}
                  />
                </div>
                <div className="cortex-welcome-headline-block">
                  <h2
                    className="cortex-welcome-headline cortex-welcome-headline--single-line"
                    aria-label={welcomeHeadline}
                    title={welcomeHeadline}
                  >
                    {welcomeHeadline}
                  </h2>
                  <time
                    className="cortex-welcome-action-time"
                    dateTime={welcomeCurrentTime.toISOString()}
                    aria-label={`Current time ${welcomeCurrentTimeLabel}`}
                  >
                    {welcomeCurrentTimeLabel}
                  </time>
                </div>
              </div>

              <div
                className="cortex-welcome-branding cortex-welcome-branding-logo-stage"
              >
                <button
                  type="button"
                  className="cortex-welcome-logo-shell cortex-welcome-logo-shell-hero cortex-welcome-logo-button"
                  aria-label={`Switch advertiser${welcomeAdvertiserName ? ` from ${welcomeAdvertiserName}` : ""}`}
                  aria-haspopup="dialog"
                  aria-expanded={advertiserSelectorOpen ? "true" : "false"}
                  aria-controls="dashboard-advertiser-selector"
                  onClick={() => setAdvertiserSelectorOpen(true)}
                >
                  <img
                    src={welcomeBrandLogoSrc}
                    alt={`${welcomeAdvertiserName || "Account"} logo`}
                    className="cortex-welcome-logo"
                    onError={() => {
                      if (welcomeBrandLogoSrc) {
                        setFailedWelcomeBrandLogos((current) =>
                          current.includes(welcomeBrandLogoSrc)
                            ? current
                            : [...current, welcomeBrandLogoSrc]
                        );
                      }
                      handleWelcomeBrandLogoError();
                    }}
                  />
                </button>
              </div>

              <button
                type="button"
                className="client-performance-open cortex-welcome-media-plan-button"
                onClick={() => navigate("/sherloc-media-planner")}
                aria-label="Build Media Plan"
              >
                BUILD MEDIA PLAN
              </button>
            </div>

            <div className="cortex-welcome-insights">
              <div className="cortex-welcome-metric-grid" aria-busy={welcomeSummaryLoading && !activeWelcomeSummary}>
                <div className="cortex-welcome-metric-card">
                  <div className="cortex-welcome-metric-label">Spend to Date</div>
                  <div className="cortex-welcome-metric-value">
                    <span className="cphere-numeric cortex-welcome-metric-number">
                      {getWelcomeDisplaySpendToDate(activeWelcomeSummary) == null ? "—" : formatMoney(getWelcomeDisplaySpendToDate(activeWelcomeSummary))}
                    </span>
                  </div>
                  <div className="cortex-welcome-metric-meta">
                    {[activeWelcomeSummary?.metrics?.spendToDateLabel, dashboardReportingCoverage(activeWelcomeSummary, welcomeSummaryRetained)].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div
                  className="cortex-welcome-metric-card"
                  data-campaigns-tile-basis={campaignsDeliveringTile.basis}
                >
                  <div className="cortex-welcome-metric-label">{campaignsDeliveringTile.label}</div>
                  <div className="cortex-welcome-metric-value">
                    <span className="cphere-numeric cortex-welcome-metric-number">
                      {campaignsDeliveringTile.value == null ? "—" : campaignsDeliveringTile.value}
                    </span>
                  </div>
                  <div className="cortex-welcome-metric-meta">{campaignsDeliveringTile.meta}</div>
                </div>
                <div className="cortex-welcome-metric-card">
                  <div className="cortex-welcome-metric-label">Platforms Live</div>
                  <div className="cortex-welcome-metric-value">
                    <span className="cphere-numeric cortex-welcome-metric-number">
                      {activeWelcomeSummary ? getWelcomeDisplayPlatformsLive(activeWelcomeSummary) : "—"}
                    </span>
                  </div>
                  <div className="cortex-welcome-metric-meta">Distinct platforms actively delivering now</div>
                </div>
              </div>

              <div className="cortex-welcome-chart-card">
                <ClientPerformancePreview
                  canonicalSummary={
                    activeWelcomeSummary?.canonicalSummary ||
                    activeWelcomeSummary?.canonicalReporting ||
                    null
                  }
                  advertiserScopedMetrics={activeWelcomeSummary?.metrics || null}
                  loading={welcomeSummaryLoading && !activeWelcomeSummary}
                  coverageLabel={dashboardReportingCoverage(activeWelcomeSummary, welcomeSummaryRetained)}
                  error={!activeWelcomeSummary ? welcomeSummaryError : null}
                  campaignsRunning={
                    activeWelcomeSummary?.metrics?.campaignsRunning ?? null
                  }
                  campaignWindowState={campaignDaysModel.state}
                  onOpen={() =>
                    navigate(
                      buildPlatformAnalyticsHandoffPath(
                        activeWelcomeSummary?.canonicalSummary ||
                          activeWelcomeSummary?.canonicalReporting ||
                          null
                      )
                    )
                  }
                />
              </div>
            </div>
          </section>

          <div className="cortex-grid" aria-label="Primary dashboard navigation">
            <button
              className="cortex-tile"
              onClick={() => navigate("/genesis-hub")}
              {...artworkIntentHandlers("/genesis-hub")}
              aria-label="Genesis Planner"
            >
              <div className="cortex-tile-face" style={{ backgroundImage: `url(${assetMap.genesisImage})` }}>
                <span className="cortex-beta-tag">PLANNER</span>
                <span>Genesis</span>
              </div>
            </button>

            <button
              className="cortex-tile"
              onClick={() => navigate("/nexus-hub")}
              aria-label="Creative Studio"
            >
              <div className="cortex-tile-face" style={{ backgroundImage: `url(${assetMap.integrationsImage})` }}>
                <span className="cortex-beta-tag">Studio</span>
                <span>Creative</span>
              </div>
            </button>

            <button
              className="cortex-tile"
              onClick={() => navigate(verticalDashboardConfig.route)}
              aria-label={verticalDashboardConfig.pageTitle}
            >
              <div
                className="cortex-tile-face"
                data-vertical={verticalDashboardConfig.vertical}
                data-tile-image-src={verticalDashboardConfig.tileImage}
                style={verticalDashboardTileStyle}
              >
                <span className="cortex-beta-tag">
                  {verticalDashboardConfig.dashboardTileBadge}
                </span>
                <span>{verticalDashboardConfig.dashboardTileLabel}</span>
              </div>
            </button>

            <button
              className="cortex-tile"
              onClick={() => navigate("/campaign-management")}
              aria-label="Platform Manager"
            >
              <div className="cortex-tile-face" style={{ backgroundImage: `url(${assetMap.cphereSocialImage})` }}>
                <span className="cortex-beta-tag">Manager</span>
                <span>Platform</span>
              </div>
            </button>

            <div className="cortex-mobile-only">
              <button
                className="cortex-tile cortex-logout-tile"
                onClick={handleLogout}
              >
                <div className="cortex-tile-face" style={{ backgroundImage: `url(${assetMap.logoutImage})` }}>
                  <span>Logout</span>
                </div>
              </button>
            </div>
          </div>

          {advertiserSelectorOpen ? (
            <DashboardAdvertiserSelector
              advertisers={visibleAdvertisers}
              selectedAdvertiserId={selectedAdvertiserRecord?.id}
              selectedAdvertiserName={selectedAdvertiserRecord?.name || selectedAdvertiser}
              apiBase={API_BASE_URL}
              onSelect={handleDashboardAdvertiserSelect}
              onClose={() => setAdvertiserSelectorOpen(false)}
            />
          ) : null}
        </div>
      </div>

      {showLoaderOverlay && (
        <div
          className={`cortex-loader-overlay ${
            fadeOutLoader ? "cortex-loader-fadeout" : ""
          }`}
        >
          <Loading active={!fadeOutLoader} />
        </div>
      )}
    </div>
  );
}

export default Dashboard;
